import { InventoryItem, RoomPhoto } from '../types';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { Browser } from '@capacitor/browser';
import { getPhotoMetadata, uploadHTMLToSupabase } from '../utils/supabaseStorage';
import { supabase } from '../lib/supabaseClient';

// Define Room interface for property inventory
interface Room {
  id?: string;
  name?: string;
  items: InventoryItem[];
}

export interface PropertyInventoryData {
  rooms: Room[];
  propertyDetails: {
    address: string;
    reportType: 'checkIn' | 'checkOut' | 'midTerm';
    reportDate: string;
    propertyType?: string;
    agent?: string;
    tenantName?: string;
    landlordName?: string;
    checkInDate?: string;
    checkOutDate?: string;
    bedrooms?: string;
    cleanliness?: string;
  };
  glossaryTerms?: Record<string, string>;
  certificates?: {
    gas?: {
      date: string;
      number: string;
      photos?: RoomPhoto[];
    };
    electrical?: {
      date: string;
      number: string;
      photos?: RoomPhoto[];
    };
  };
  keys?: Array<{
    handedOver: string;
    type: string;
    quantity: string;
    photos?: RoomPhoto[];
  }>;
  meterReadings?: {
    gas?: {
      location: string;
      serialNo: string;
      reading: string;
      photos?: RoomPhoto[];
    };
    electricity?: {
      location: string;
      serialNo: string;
      reading: string;
      photos?: RoomPhoto[];
    };
    water?: {
      location: string;
      serialNo: string;
      reading: string;
      photos?: RoomPhoto[];
    };
  };
  signatures?: {
    landlord?: string;
    agent?: string;
    customer?: string;
    assessor?: string;
    assessorDate?: string;
    customerDate?: string;
  };
}

export interface HTMLInventoryOptions {
  format?: 'A4' | 'A3' | 'Letter';
  orientation?: 'portrait' | 'landscape';
  printBackground?: boolean;
  logoDataUrl?: string;
  headerColor?: string;
  formatChoice?: 'pdf' | 'html' | 'both';
  companyName?: string;
  companyWebsite?: string;
  companyPhone?: string;
  companyEmail?: string;
  companyAddress?: string;
}

/**
 * HTML-based Property Inventory PDF Generator
 * Converts jsPDF inventory reports to modern HTML/CSS with better styling
 */
export class HTMLInventoryPDFService {
  private static defaultOptions: HTMLInventoryOptions = {
    format: 'A4',
    orientation: 'portrait',
    printBackground: true
  };

  private static photoUrlLogCount = 0; // Track how many URLs we've logged

  /**
   * Get photo URL for export (Supabase public URL or fallback)
   * Photos loaded from DB already have photo.url set - use those directly
   */
  private static async getPhotoUrl(photo: RoomPhoto): Promise<string> {
    // If photo already has a valid URL, use it directly (most common case)
    // This avoids path construction issues for photos loaded from the database
    if (photo.url && (photo.url.startsWith('http://') || photo.url.startsWith('https://'))) {
      if (this.photoUrlLogCount < 3) {
        console.log(`📸 Photo ${this.photoUrlLogCount + 1} using existing URL:`, photo.url);
        this.photoUrlLogCount++;
      }
      return photo.url;
    }
    
    // Only construct path if URL is missing or invalid
    // This is mainly for newly captured photos that haven't been saved yet
    if (photo.fileName && photo.fileName.includes('/')) {
      // Get current user email to construct correct storage path
      const { data: { user } } = await supabase.auth.getUser();
      const userEmail = user?.email;
      
      if (!userEmail) {
        console.error('❌ [getPhotoUrl] No user email found, cannot construct URL');
        return photo.localPath || '';
      }
      
      // Sanitize email to match upload path format
      const sanitizedEmail = userEmail
        .toLowerCase()
        .trim()
        .replace(/@/g, '-at-')
        .replace(/\./g, '-')
        .replace(/[^a-z0-9-]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '')
        .substring(0, 50);
      
      // Check if fileName already has the email prefix to avoid double-prefixing
      const fullPath = photo.fileName.startsWith(sanitizedEmail) 
        ? photo.fileName 
        : `${sanitizedEmail}/${photo.fileName}`;
      
      const { data } = supabase.storage
        .from('inventory-photos')
        .getPublicUrl(fullPath);
      
      if (this.photoUrlLogCount < 3) {
        console.log(`📸 Photo ${this.photoUrlLogCount + 1} constructed URL:`, data.publicUrl);
        console.log(`📸 Photo ${this.photoUrlLogCount + 1} from path:`, fullPath);
        this.photoUrlLogCount++;
      }
      
      return data.publicUrl;
    }
    
    // Final fallback to localPath
    const fallbackUrl = photo.localPath || '';
    
    if (this.photoUrlLogCount < 3 && fallbackUrl) {
      console.log(`📸 Photo ${this.photoUrlLogCount + 1} URL (localPath fallback):`, fallbackUrl);
      this.photoUrlLogCount++;
    }
    
    return fallbackUrl;
  }

  /**
   * Main export function for property inventory
   */
  static async exportInventoryToPDF(
    data: PropertyInventoryData,
    filename: string = 'property-inventory.pdf',
    options: HTMLInventoryOptions = {},
    userChoice: 'pdf' | 'html' | 'both' = 'html'
  ): Promise<void> {
    // Reset photo URL logging counter for fresh debug logs
    this.photoUrlLogCount = 0;
    
    // DEBUG: Log what data is received
    console.log('📋 HTMLInventoryPDFService - Received Data:', {
      reportDate: data.propertyDetails.reportDate,
      address: data.propertyDetails.address,
      reportType: data.propertyDetails.reportType,
      checkInDate: data.propertyDetails.checkInDate,
      checkOutDate: data.propertyDetails.checkOutDate,
      fullPropertyDetails: data.propertyDetails
    });
    
    // Show progress modal IMMEDIATELY before any processing
    const { progressDiv, backdrop, updateProgress, isCancelledRef, modalStartTime } = this.showProgressModal();
    
    try {
      const mergedOptions = { ...this.defaultOptions, ...options };
      
      // Now do the heavy processing after user choice
      updateProgress(5, 'Counting photos...');
      
      // Quick photo count (no metadata fetching - much faster!)
      let totalPhotoCount = 0;
      data.rooms.forEach(room => {
        room.items?.forEach((item: InventoryItem) => {
          const photos = item.photos?.filter((p: RoomPhoto) => 
            p && !p.uploading && (p.url || p.localPath)
          ) || [];
          totalPhotoCount += photos.length;
        });
      });
      
      console.log(`📸 Found ${totalPhotoCount} photos in inventory`);
      
      updateProgress(10, 'Building document structure...');
      
      console.log('🔨 Generating HTML content...');
      
      // Generate HTML content with custom color, logo, and company details
      let htmlContent: string;
      try {
        htmlContent = await this.generateInventoryHTML(
          data, 
          options.headerColor || '#009688', 
          options.logoDataUrl,
          options.companyName,
          options.companyWebsite,
          options.companyPhone,
          options.companyEmail,
          options.companyAddress
        );
      } catch (htmlError) {
        console.error('❌ HTML generation failed:', htmlError);
        throw new Error(`HTML generation failed: ${(htmlError as Error).message}`);
      }
      
      // Different progress paths for HTML vs PDF
      if (userChoice === 'html') {
        updateProgress(30, 'HTML structure complete...');
      } else {
        updateProgress(18, 'Starting PDF generation...');
      }
      
      // Generate PDF using jsPDF with HTML rendering (pass existing modal)
      await this.generatePDFFromHTML(
        htmlContent, 
        filename, 
        mergedOptions, 
        userChoice,
        { progressDiv, updateProgress, isCancelledRef },
        { totalPhotoCount, totalPhotoSize: 0 },
        data
      );
      
      console.log('✅ Property Inventory PDF generated successfully');
      
    } catch (error) {
      console.error('❌ Error exporting property inventory PDF:', error);
      
      // Clean up progress modal on error
      if (progressDiv && progressDiv.parentNode) {
        progressDiv.parentNode.removeChild(progressDiv);
      }
      if (backdrop && backdrop.parentNode) {
        backdrop.parentNode.removeChild(backdrop);
      }
      
      throw error;
    }
  }

  /**
   * Count photo pages for certificates, keys, and meter readings
   * Each page holds maximum 4 photos (2x2 grid)
   */
  private static countCertificatePhotoPages(
    certificates?: any,
    keys?: Array<any>,
    meterReadings?: any
  ): number {
    let totalPhotos = 0;

    // Count gas certificate photos
    if (certificates?.gas?.photos?.length > 0) {
      const validPhotos = certificates.gas.photos.filter((p: any) => 
        p && !p.uploading && (p.url || p.localPath)
      );
      totalPhotos += validPhotos.length;
    }

    // Count electrical certificate photos
    if (certificates?.electrical?.photos?.length > 0) {
      const validPhotos = certificates.electrical.photos.filter((p: any) => 
        p && !p.uploading && (p.url || p.localPath)
      );
      totalPhotos += validPhotos.length;
    }

    // Count keys photos
    if (keys && Array.isArray(keys)) {
      keys.forEach((key: any) => {
        if (key.photos?.length > 0) {
          const validPhotos = key.photos.filter((p: any) => 
            p && !p.uploading && (p.url || p.localPath)
          );
          totalPhotos += validPhotos.length;
        }
      });
    }

    // Count meter readings photos
    ['gas', 'electricity', 'water'].forEach(meterType => {
      const meter = meterReadings?.[meterType];
      if (meter?.photos?.length > 0) {
        const validPhotos = meter.photos.filter((p: any) => 
          p && !p.uploading && (p.url || p.localPath)
        );
        totalPhotos += validPhotos.length;
      }
    });

    // Each page holds 4 photos (2x2 grid), round up
    return totalPhotos > 0 ? Math.ceil(totalPhotos / 4) : 0;
  }

  /**
   * Count total photo pages needed based on actual photo page generation logic
   * Each page holds maximum 4 photos (2x2 grid)
   */
  private static countPhotoPages(rooms: Room[]): number {
    const roomsWithPhotos = rooms.filter(room => 
      room.items?.some((item: InventoryItem) => 
        item.photos?.length > 0 && item.photos.some((p: RoomPhoto) => 
          p && !p.uploading && (p.url || p.localPath)
        )
      )
    );

    if (roomsWithPhotos.length === 0) {
      return 0;
    }

    let totalPhotoPages = 0;

    roomsWithPhotos.forEach(room => {
      const itemsWithPhotos = room.items.filter((item: InventoryItem) => 
        item?.photos?.length > 0 && item.photos.some((p: RoomPhoto) => 
          p && !p.uploading && (p.url || p.localPath)
        )
      );

      // Group items by category
      const itemsByCategory = itemsWithPhotos.reduce((acc: Record<string, InventoryItem[]>, item: InventoryItem) => {
        const category = item.category || 'Uncategorized';
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(item);
        return acc;
      }, {} as Record<string, InventoryItem[]>);

      // Count photos per category and calculate pages (4 photos per page)
      Object.values(itemsByCategory).forEach((categoryItems: InventoryItem[]) => {
        let categoryPhotoCount = 0;
        categoryItems.forEach((item: InventoryItem) => {
          const validPhotos = item.photos?.filter((p: RoomPhoto) => 
            p && !p.uploading && (p.url || p.localPath)
          ) || [];
          categoryPhotoCount += validPhotos.length;
        });
        
        // Calculate pages needed: 4 photos per page (round up)
        if (categoryPhotoCount > 0) {
          totalPhotoPages += Math.ceil(categoryPhotoCount / 4);
        }
      });
    });

    return totalPhotoPages;
  }

  /**
   * Convert hex color to RGB object for jsPDF
   */
  private static hexToRgb(hex: string): { r: number; g: number; b: number } {
    const sanitized = hex.replace('#', '');
    const r = parseInt(sanitized.substring(0, 2), 16);
    const g = parseInt(sanitized.substring(2, 4), 16);
    const b = parseInt(sanitized.substring(4, 6), 16);
    return { r, g, b };
  }

  /**
   * Generate gradient color variations from a base color (preserves hue)
   */
  private static generateColorPalette(baseColor: string) {
    // Parse hex color
    const hex = baseColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;
    
    // Convert RGB to HSL to preserve hue
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;
    
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
      }
    }
    
    // Convert HSL back to RGB with adjusted lightness
    const hslToRgb = (h: number, s: number, l: number): string => {
      let r: number, g: number, b: number;
      
      if (s === 0) {
        r = g = b = l;
      } else {
        const hue2rgb = (p: number, q: number, t: number) => {
          if (t < 0) t += 1;
          if (t > 1) t -= 1;
          if (t < 1/6) return p + (q - p) * 6 * t;
          if (t < 1/2) return q;
          if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
          return p;
        };
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1/3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1/3);
      }
      
      const toHex = (x: number) => Math.round(x * 255).toString(16).padStart(2, '0');
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    };
    
    // Generate palette with same hue, varying lightness
    return {
      50: hslToRgb(h, s, 0.97),
      100: hslToRgb(h, s, 0.94),
      200: hslToRgb(h, s, 0.88),
      300: hslToRgb(h, s, 0.76),
      400: hslToRgb(h, s, 0.64),
      500: baseColor, // Base color
      600: hslToRgb(h, s, Math.max(0.1, l - 0.1)),
      700: hslToRgb(h, s, Math.max(0.1, l - 0.2)),
      800: hslToRgb(h, s, Math.max(0.1, l - 0.3)),
      900: hslToRgb(h, s, Math.max(0.1, l - 0.4)),
    };
  }

  /**
   * Generate HTML for non-photo pages only (optimized path)
   */
  private static async generateNonPhotoHTML(
    data: PropertyInventoryData,
    colors: ReturnType<typeof HTMLInventoryPDFService.generateColorPalette>,
    logoDataUrl?: string,
    companyName?: string,
    companyWebsite?: string,
    companyPhone?: string,
    totalPages?: number,
    companyEmail?: string,
    companyAddress?: string
  ): Promise<string> {
    const { propertyDetails, rooms, glossaryTerms, certificates, keys, meterReadings, signatures } = data;
    
    // Pre-generate certificates section to avoid async in template literals
    const certificatesContent = (certificates || keys || meterReadings) 
      ? await this.generateCertificatesSection(certificates, keys, meterReadings)
      : '';
    
    // Calculate page numbers (excluding photo pages)
    let currentPage = 1;
    
    // Calculate TOC pages for accurate total
    const contentsItems = this.generateContentsItems(data, 0);
    const tocPages = Math.ceil(contentsItems.length / 13);
    
    const calculatedTotalPages = totalPages || (
      1 + // Property details
      tocPages + // TOC (may be multiple pages)
      (glossaryTerms ? 1 : 0) +
      (certificates || keys || meterReadings ? 1 : 0) +
      this.countCertificatePhotoPages(certificates, keys, meterReadings) + // Certificate/keys/meter photos
      rooms.length +
      this.countPhotoPages(rooms) +
      1 // Signatures
    );
    
    return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <title>Property Inventory Report - ${propertyDetails.address}</title>
        ${this.getCSS(colors)}
      </head>
      <body>
        <!-- Page 1: Property Details -->
        <section class="pdf-page">
          ${this.generateHeaderSection(propertyDetails, currentPage++, calculatedTotalPages, logoDataUrl, companyName, companyWebsite, companyPhone)}
          ${this.generatePropertyDetailsSection(propertyDetails)}
        </section>
        
        <!-- Page 2: Table of Contents -->
        <section class="pdf-page">
          ${this.generateHeaderSection(propertyDetails, currentPage++, calculatedTotalPages, logoDataUrl, companyName, companyWebsite, companyPhone)}
          ${this.generateContentsSection(data, calculatedTotalPages, tocPages)}
        </section>
        
        <!-- Page 3: Glossary (if exists) -->
        ${glossaryTerms ? `
        <section class="pdf-page">
          ${this.generateHeaderSection(propertyDetails, currentPage++, calculatedTotalPages, logoDataUrl, companyName, companyWebsite, companyPhone)}
          <div id="glossary">${this.generateGlossarySection(glossaryTerms)}</div>
        </section>` : ''}
        
          ${this.generateHeaderSection(propertyDetails, calculatedTotalPages, calculatedTotalPages, logoDataUrl, companyName, companyWebsite, companyPhone)}
          ${this.generateSignaturesSection(signatures, colors, propertyDetails, companyName, companyWebsite, companyPhone, companyEmail, companyAddress)}
        </section>
      </body>
      </html>
    `;
  }

  /**
   * Generate complete HTML template for property inventory
   */
  private static async generateInventoryHTML(
    data: PropertyInventoryData, 
    headerColor: string = '#009688', 
    logoDataUrl?: string,
    companyName?: string,
    companyWebsite?: string,
    companyPhone?: string,
    companyEmail?: string,
    companyAddress?: string
  ): Promise<string> {
    // Generate color palette from user's choice
    const colors = this.generateColorPalette(headerColor);
    const { propertyDetails, rooms, glossaryTerms, certificates, keys, meterReadings, signatures } = data;
    
    // Pre-generate certificates section to avoid async in template literals
    const certificatesContent = (certificates || keys || meterReadings) 
      ? await this.generateCertificatesSection(certificates, keys, meterReadings)
      : '';
    
    // Pre-generate certificate photos section
    const certificatePhotoPages = this.countCertificatePhotoPages(certificates, keys, meterReadings);
    const certificatePhotosContent = certificatePhotoPages > 0
      ? await this.generateCertificatePhotosSection(
          certificates, 
          keys, 
          meterReadings, 
          propertyDetails, 
          0, // startPage will be set correctly in template
          0, // totalPages will be set correctly in template
          logoDataUrl,
          companyName,
          companyWebsite,
          companyPhone
        )
      : '';
    
    // Calculate total pages accurately
    let totalPages = 1; // Page 1: Property Details
    
    // ⚡ PERFORMANCE: Calculate TOC items once and cache the count
    // This avoids recalculating the same thing multiple times
    const initialTocItems = this.generateContentsItems(data, 0);
    const tocPages = Math.ceil(initialTocItems.length / 13);
    totalPages += tocPages; // Table of Contents (may be multiple pages)
    
    if (glossaryTerms) totalPages++; // Glossary
    if (certificates || keys || meterReadings) totalPages++; // Certificates/Keys/Meters
    
    // Add certificate/keys/meter photo pages (already calculated above)
    totalPages += certificatePhotoPages;
    
    // Calculate actual inventory pages (accounting for multi-page inventories)
    const inventoryPages = rooms.reduce((total, room) => {
      const itemCount = room.items?.length || 0;
      const pagesForRoom = itemCount === 0 ? 1 : Math.ceil(itemCount / 15);
      return total + pagesForRoom;
    }, 0);
    totalPages += inventoryPages;
    
    totalPages += this.countPhotoPages(rooms); // Photo pages (one per room category with photos)
    totalPages++; // Signatures page
    
    let currentPage = 1;
    
    return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0, minimum-scale=0.3, maximum-scale=2.0, user-scalable=yes">
        <meta http-equiv="Content-Security-Policy" content="default-src 'self' 'unsafe-inline' 'unsafe-eval' data: blob: https://cdnjs.cloudflare.com https://qazhweunmfzifeenbozk.supabase.co; img-src 'self' data: blob: https://*;">
        <title>Property Inventory Report - ${propertyDetails.address}</title>
        <!-- PDF Generation Libraries -->
        <script src="https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js"></script>
        <script src="https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js"></script>
        <script>
          // Image load diagnostics
          window.addEventListener('DOMContentLoaded', () => {
            const images = document.querySelectorAll('img');
            console.log('Total images in document:', images.length);
            
            // Log first 3 image URLs for debugging
            images.forEach((img, i) => {
              if (i < 3) {
                console.log(\`Image \${i + 1} URL:\`, img.src);
              }
              
              img.addEventListener('error', function() {
                console.error('Failed to load image:', this.src);
              });
              
              img.addEventListener('load', function() {
                if (i < 3) console.log(\`Image \${i + 1} loaded successfully\`);
              });
            });
          });
        </script>
        ${this.getCSS(colors)}
      </head>
      <body>
        <!-- Download PDF Button -->
        <button id="download-pdf-btn" class="download-pdf-button" onclick="downloadAsPDF()" title="Download as PDF">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          <span id="pdf-btn-text">Download PDF</span>
          <div id="pdf-progress" style="display: none; margin-left: 8px;">
            <div style="width: 16px; height: 16px; border: 2px solid #fff; border-top-color: transparent; border-radius: 50%; animation: spin 1s linear infinite;"></div>
          </div>
        </button>
        <!-- Page 1: Header + Property Details -->
        <section class="pdf-page">
          ${this.generateHeaderSection(propertyDetails, currentPage++, totalPages, logoDataUrl, companyName, companyWebsite, companyPhone)}
          ${this.generatePropertyDetailsSection(propertyDetails)}
        </section>
        
        <!-- Pages 2+: Table of Contents (may span multiple pages) -->
        ${this.generateContentsPagesSection(data, propertyDetails, currentPage, totalPages, logoDataUrl, companyName, companyWebsite, companyPhone)}
        ${(() => { currentPage += tocPages; return ''; })()}
        
        <!-- Glossary of Terms -->
        ${glossaryTerms ? (() => {
          const glossaryPageNum = currentPage;
          return `
        <section class="pdf-page" id="page-${glossaryPageNum}">
          ${this.generateHeaderSection(propertyDetails, currentPage++, totalPages, logoDataUrl, companyName, companyWebsite, companyPhone)}
          <div id="glossary">${this.generateGlossarySection(glossaryTerms)}</div>
        </section>`;
        })() : ''}
        
        <!-- Certificates, Keys, Meter Readings -->
        ${certificatesContent ? (() => {
          const certPageNum = currentPage;
          return `
        <section class="pdf-page" id="page-${certPageNum}">
          ${this.generateHeaderSection(propertyDetails, currentPage++, totalPages, logoDataUrl, companyName, companyWebsite, companyPhone)}
          <div id="certificates">${certificatesContent}</div>
        </section>`;
        })() : ''}
        
        <!-- Page 4+: Room Inventories (each room on separate page) -->
        ${await this.generateRoomInventorySection(rooms, propertyDetails, currentPage, totalPages, logoDataUrl, companyName, companyWebsite, companyPhone)}
        
        <!-- Photos Section (disabled for mid-term reports) -->
        ${propertyDetails.reportType !== 'midTerm' ? await (async () => {
          // Calculate photo pages
          const inventoryPages = rooms.reduce((total: number, room: Room) => {
            const itemCount = room.items?.length || 0;
            const pagesForRoom = itemCount === 0 ? 1 : Math.ceil(itemCount / 15);
            return total + pagesForRoom;
          }, 0);
          return await this.generatePhotosSection(rooms, propertyDetails, currentPage + inventoryPages, totalPages, logoDataUrl, companyName, companyWebsite, companyPhone);
        })() : ''}
        
        <!-- Signatures Section -->
        <section class="pdf-page" id="signatures">
          ${this.generateHeaderSection(propertyDetails, totalPages, totalPages, logoDataUrl, companyName, companyWebsite, companyPhone)}
          ${this.generateSignaturesSection(signatures, colors, propertyDetails, companyName, companyWebsite, companyPhone, companyEmail, companyAddress)}
        </section>

        <!-- Lightbox Modal -->
        <div id="lightbox-modal" class="lightbox-modal">
          <span class="lightbox-close" onclick="closeLightbox()">&times;</span>
          <img id="lightbox-img" class="lightbox-content" src="" alt="">
          <div id="lightbox-caption" class="lightbox-caption"></div>
        </div>

        <!-- LIGHTBOX SCRIPT VERSION: 2026-01-10-v5 -->
        <script>
          console.log('[v5] Lightbox script tag executing...');
          
          // Lightbox functionality
          function openLightbox(imgElement) {
            console.log('openLightbox called for:', imgElement.alt);
            const modal = document.getElementById('lightbox-modal');
            const modalImg = document.getElementById('lightbox-img');
            const caption = document.getElementById('lightbox-caption');
            
            if (!modal || !modalImg || !caption) {
              console.error('ERROR: Lightbox elements missing - modal:', modal, 'modalImg:', modalImg, 'caption:', caption);
              return;
            }
            
            console.log('Opening lightbox with image:', imgElement.src);
            modal.classList.add('active');
            modalImg.src = imgElement.src;
            caption.textContent = imgElement.alt || 'Photo';
          }

          function closeLightbox() {
            const modal = document.getElementById('lightbox-modal');
            modal.classList.remove('active');
          }

          // Add click listeners to all images  
          // Execute immediately - DOM is already loaded since script is at end of body
          console.log('Starting lightbox initialization IIFE...');
          (function() {
            console.log('Inside IIFE - initializing lightbox...');
            const modal = document.getElementById('lightbox-modal');
            const images = document.querySelectorAll('.photo-container img, .photo-grid img');
            
            if (!modal) {
              console.error('ERROR: Lightbox modal not found!');
              return;
            }
            
            console.log('Lightbox initialized: Found', images.length, 'photos');
            
            images.forEach(function(img) {
              // Add both click and touch events for mobile compatibility
              img.addEventListener('click', function(e) {
                console.log('Photo clicked:', this.alt);
                e.preventDefault();
                e.stopPropagation();
                openLightbox(this);
              });
              
              img.addEventListener('touchend', function(e) {
                e.preventDefault();
                e.stopPropagation();
                openLightbox(this);
              });
              
              // Make images tappable on mobile
              img.style.cursor = 'pointer';
              img.style.webkitTapHighlightColor = 'rgba(0, 0, 0, 0.1)';
              img.style.touchAction = 'manipulation';
            });

            // Close lightbox when clicking outside the image
            modal.addEventListener('click', function(e) {
              if (e.target === this) {
                closeLightbox();
              }
            });
            
            modal.addEventListener('touchend', function(e) {
              if (e.target === this) {
                closeLightbox();
              }
            });

            // Close lightbox with Escape key
            document.addEventListener('keydown', function(e) {
              if (e.key === 'Escape') {
                closeLightbox();
              }
            });
          })(); // Execute IIFE immediately

          // Download as PDF Function
          async function downloadAsPDF() {
            const button = document.getElementById('download-pdf-btn');
            const progressDiv = document.getElementById('pdf-progress');
            const buttonText = document.getElementById('pdf-btn-text');
            
            if (!button || !progressDiv || !buttonText) {
              console.error('Required elements not found');
              alert('PDF generation failed: UI elements not found');
              return;
            }
            
            try {
              // Show loading state
              button.classList.add('generating');
              button.disabled = true;
              buttonText.textContent = 'Generating...';
              progressDiv.style.display = 'block';
              progressDiv.textContent = '0%';

              // Get all pages
              const pages = document.querySelectorAll('.pdf-page');
              
              if (!pages || pages.length === 0) {
                throw new Error('No pages found to generate PDF');
              }
              
              // Check if jsPDF is loaded
              if (!window.jspdf || !window.jspdf.jsPDF) {
                throw new Error('PDF library not loaded. Please refresh the page.');
              }
              
              // Use jsPDF from CDN
              const { jsPDF } = window.jspdf;
              const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4',
                compress: true
              });

              // A4 dimensions
              const pdfWidth = 210; // A4 width in mm
              const pdfHeight = 297; // A4 height in mm

              // Process pages sequentially for maximum speed and memory efficiency
              const pageArray = Array.from(pages);
              const totalPages = pageArray.length;
              
              const canvasOptions = {
                scale: 2.5, // High quality for premium output
                useCORS: true,
                allowTaint: false,
                backgroundColor: 'white',
                logging: false,
                imageTimeout: 0,
                removeContainer: true,
                foreignObjectRendering: false,
                ignoreElements: (element) => element.classList.contains('no-export')
              };
              
              // Process each page one at a time
              for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
                const page = pageArray[pageIndex];
                
                // Update progress
                const progress = Math.round((pageIndex / totalPages) * 100);
                progressDiv.textContent = progress + '%';
                buttonText.textContent = 'Page ' + (pageIndex + 1) + '/' + totalPages;
                
                try {
                  // Check if html2canvas is available
                  if (typeof html2canvas !== 'function') {
                    throw new Error('HTML2Canvas library not loaded');
                  }
                  
                  const canvas = await html2canvas(page, {
                    ...canvasOptions,
                    width: page.scrollWidth,
                    height: page.scrollHeight
                  });
                  
                  if (canvas) {
                    const imgData = canvas.toDataURL('image/jpeg', 0.88);
                    
                    if (pageIndex > 0) {
                      pdf.addPage();
                    }
                    
                    pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
                    
                    // Free memory immediately
                    const ctx = canvas.getContext('2d');
                    if (ctx) {
                      ctx.clearRect(0, 0, canvas.width, canvas.height);
                    }
                    canvas.width = 1;
                    canvas.height = 1;
                  } else {
                    console.warn('Canvas generation returned null for page ' + (pageIndex + 1));
                    if (pageIndex > 0) {
                      pdf.addPage();
                    }
                  }
                } catch (error) {
                  console.error('Error rendering page ' + (pageIndex + 1) + ':', error);
                  // Add blank page to maintain page count
                  if (pageIndex > 0) {
                    pdf.addPage();
                  }
                }
                
                // Brief pause to prevent UI freezing
                await new Promise(resolve => setTimeout(resolve, 10));
              }
              
              progressDiv.textContent = '100%';
              buttonText.textContent = 'Saving...';

              // Generate filename with fallback
              const title = document.title ? document.title.replace(/[^a-z0-9]/gi, '-').toLowerCase() : 'inventory';
              const filename = title + '-' + Date.now() + '.pdf';
              
              // Download with error handling
              try {
                pdf.save(filename);
                console.log('PDF downloaded successfully:', filename);
              } catch (saveError) {
                console.error('Error saving PDF:', saveError);
                throw new Error('Failed to save PDF file');
              }
              
              // Success state
              progressDiv.textContent = '✓';
              progressDiv.style.color = '#10b981';
              buttonText.textContent = 'PDF Downloaded!';
              button.style.background = 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
              button.classList.remove('generating');
              
              setTimeout(() => {
                buttonText.textContent = 'Download PDF';
                progressDiv.style.display = 'none';
                progressDiv.style.color = '';
                button.style.background = '';
                button.disabled = false;
              }, 3000);
            } catch (error) {
              console.error('Error generating PDF:', error);
              
              // Error state with detailed message
              const errorMessage = error.message || 'Unknown error';
              progressDiv.textContent = '✗';
              progressDiv.style.color = '#ef4444';
              buttonText.textContent = 'Error: ' + errorMessage;
              button.style.background = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
              button.classList.remove('generating');
              
              // Show user-friendly alert
              alert('PDF generation failed: ' + errorMessage + '\\n\\nPlease try again. If the problem persists, try refreshing the page.');
              
              // Reset to retry state after 3 seconds
              setTimeout(() => {
                buttonText.textContent = 'Retry PDF Download';
                progressDiv.style.display = 'none';
                progressDiv.style.color = '';
                button.style.background = '';
                button.disabled = false;
              }, 3000);
            }
          }
        </script>
      </body>
      </html>
    `;
  }

  /**
   * Generate modern CSS with design system and advanced HTML capabilities
   */
  private static getCSS(colors: ReturnType<typeof HTMLInventoryPDFService.generateColorPalette>): string {
    return `
      <style>
        /* Design System - CSS Custom Properties */
        :root {
          /* Color Palette */
          --primary-50: ${colors[50]};
          --primary-100: ${colors[100]};
          --primary-200: ${colors[200]};
          --primary-300: ${colors[300]};
          --primary-400: ${colors[400]};
          --primary-500: ${colors[500]};
          --primary-600: ${colors[600]};
          --primary-700: ${colors[700]};
          --primary-800: ${colors[800]};
          --primary-900: ${colors[900]};
          
          /* Grayscale */
          --gray-50: #fafafa;
          --gray-100: #f5f5f5;
          --gray-200: #eeeeee;
          --gray-300: #e0e0e0;
          --gray-400: #bdbdbd;
          --gray-500: #9e9e9e;
          --gray-600: #757575;
          --gray-700: #616161;
          --gray-800: #424242;
          --gray-900: #212121;
          
          /* Typography Scale */
          --text-xs: 0.75rem;
          --text-sm: 0.875rem;
          --text-base: 1rem;
          --text-lg: 1.125rem;
          --text-xl: 1.25rem;
          --text-2xl: 1.5rem;
          --text-3xl: 1.875rem;
          --text-4xl: 2.25rem;
          
          /* Spacing Scale */
          --space-1: 0.25rem;
          --space-2: 0.5rem;
          --space-3: 0.75rem;
          --space-4: 1rem;
          --space-5: 1.25rem;
          --space-6: 1.5rem;
          --space-8: 2rem;
          --space-10: 2.5rem;
          --space-12: 3rem;
          --space-16: 4rem;
          
          /* Border Radius */
          --radius-sm: 0.125rem;
          --radius-md: 0.375rem;
          --radius-lg: 0.5rem;
          --radius-xl: 0.75rem;
          --radius-2xl: 1rem;
          
          /* Shadows */
          --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
          --shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
          --shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
          --shadow-xl: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
          
          /* Modern Font Stack */
          --font-sans: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
          --font-mono: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
        }

        /* Print and Page Setup */
        @page {
          size: A4;
          margin: 0;
        }

        /* Modern Base Styles */
        * {
          box-sizing: border-box;
        }

        body {
          font-family: var(--font-sans);
          line-height: 1.6;
          color: var(--gray-800);
          background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
          margin: 0;
          padding: 0;
          font-feature-settings: "kern" 1, "liga" 1, "calt" 1;
          text-rendering: optimizeLegibility;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        @media print {
          body { 
            -webkit-print-color-adjust: exact; 
            color-adjust: exact;
            margin: 0;
            padding: 0;
            background: white;
          }
          .pdf-page {
            page-break-before: always;
            page-break-inside: avoid;
            min-height: 297mm; /* Exact A4 height */
            width: 210mm; /* Exact A4 width */
            padding: 6mm; /* Minimal margin for maximum content */
            margin: 0;
            box-sizing: border-box;
            box-shadow: none; /* Remove shadow for print */
          }
          .pdf-page:first-child {
            page-break-before: avoid;
          }
          .page-break { 
            page-break-before: always; 
          }
          .no-break { 
            page-break-inside: avoid; 
          }
        }

        /* Base Styles - Match jsPDF exactly */
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: 'Helvetica', 'Arial', sans-serif;
          font-size: 10pt; /* exact normalSize from jsPDF */
          line-height: 1.4;
          color: #333333; /* exact textColor from jsPDF */
          background: white;
          margin: 0;
          padding: 30px 0; /* Padding top and bottom for better viewing */
        }

        /* Page Structure - Maximized for full A4 usage */
        .pdf-page {
          padding: 6mm; /* Minimal margin for maximum content */
          background: white;
          min-height: 297mm; /* Minimum A4 height, can expand */
          width: 210mm; /* A4 width */
          margin: 0 auto 30px auto; /* 30px gap between pages for screen viewing */
          position: relative;
          box-sizing: border-box;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1); /* Subtle shadow for depth */
        }
        
        .header {
          background: linear-gradient(135deg, var(--primary-700) 0%, var(--primary-600) 50%, var(--primary-500) 100%);
          color: white;
          padding: 10px 14px;
          border-radius: var(--radius-lg);
          margin-bottom: 8px;
          box-shadow: var(--shadow-lg);
          position: relative;
          overflow: hidden;
        }
        
        .header::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, transparent 50%, rgba(0, 0, 0, 0.05) 100%);
          pointer-events: none;
        }
        
        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          position: relative;
          z-index: 2;
        }
        
        .header-left {
          display: flex;
          align-items: center;
          gap: var(--space-3);
        }
        
        .header-logo {
          max-width: 80px;
          max-height: 40px;
          width: auto;
          height: auto;
          object-fit: contain;
          background: rgba(255, 255, 255, 0.1);
          padding: var(--space-2);
          border-radius: var(--radius-md);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .header-text {
          flex-grow: 1;
        }
        
        .header-title {
          font-size: var(--text-xl);
          font-weight: 700;
          margin: 0 0 var(--space-1) 0;
          line-height: 1.2;
          letter-spacing: -0.025em;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        }
        
        .header-company {
          font-size: var(--text-xs);
          opacity: 0.9;
          margin: 0;
          font-weight: 500;
          line-height: 1.3;
        }
        
        .header-website {
          font-size: var(--text-xs);
          opacity: 0.8;
          margin: 0;
          font-weight: 400;
          line-height: 1.3;
          font-family: var(--font-mono);
        }
        
        .header-page {
          color: white;
          font-size: var(--text-xs);
          font-weight: 400;
          line-height: 1.3;
          opacity: 0.8;
          text-align: right;
          white-space: nowrap;
        }
        
        .header-line {
          height: 2px;
          background: linear-gradient(90deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.1) 100%);
          margin-top: var(--space-2);
          border-radius: var(--radius-sm);
        }

        /* Section Headers - Modern Design System */
        .section-header {
          background: linear-gradient(135deg, var(--primary-700) 0%, var(--primary-600) 100%);
          color: white;
          padding: 8px 14px;
          border-radius: var(--radius-md);
          font-weight: 700;
          font-size: var(--text-lg);
          margin: 0 0 8px 0;
          min-height: 32px;
          line-height: 1.2;
          display: flex;
          align-items: center;
          box-shadow: var(--shadow-md);
          position: relative;
          overflow: hidden;
        }

        .section-header::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 50%, rgba(0, 0, 0, 0.05) 100%);
          pointer-events: none;
        }

        .subsection-header {
          background: #f8f9fa;
          border: 1px solid #e5e7eb;
          color: #374151;
          padding: 10px 14px;
          border-radius: 8px;
          margin: 8px 0;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          font-family: var(--font-sans);
        }

        .subsection-title {
          font-size: 16px;
          font-weight: 700;
          margin-bottom: 8px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #1f2937;
        }

        .subsection-details {
          font-size: 13px;
          color: #6b7280;
          line-height: 1.4;
        }

        .subsection-condition {
          font-weight: 600;
          margin-bottom: 4px;
        }

        .subsection-notes {
          font-style: italic;
          color: #9ca3af;
        }

        .subsection-header::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, transparent 50%, rgba(0, 0, 0, 0.03) 100%);
          pointer-events: none;
        }

        /* Property Details - Modern Design System */
        .property-details-section {
          background: linear-gradient(135deg, white 0%, var(--gray-50) 100%);
          border: 1px solid var(--primary-200);
          border-radius: var(--radius-xl);
          padding: var(--space-6);
          margin-bottom: var(--space-4);
          box-shadow: var(--shadow-lg);
          position: relative;
          overflow: hidden;
          min-height: 120px;
          box-sizing: border-box;
        }

        .property-description-section {
          background: linear-gradient(135deg, white 0%, var(--gray-50) 100%);
          border: 1px solid var(--primary-200);
          border-radius: var(--radius-xl);
          padding: var(--space-6);
          margin-bottom: var(--space-4);
          box-shadow: var(--shadow-lg);
          position: relative;
          overflow: hidden;
          min-height: 100px;
          box-sizing: border-box;
        }

        /* Subtle border accent with design system colors */
        .property-details-section::before,
        .property-description-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, var(--primary-500), var(--primary-300));
          border-radius: var(--radius-xl) var(--radius-xl) 0 0;
        }

        .section-title {
          font-size: var(--text-xl);
          font-weight: 600;
          color: var(--primary-700);
          margin: 0 0 var(--space-4) 0;
          line-height: 1.3;
          position: relative;
          letter-spacing: 0.025em;
          text-transform: uppercase;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        /* Property Details Content - Advanced CSS Grid Layout */
        .property-details-content,
        .property-description-content {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: var(--space-5);
          margin-top: 8px;
          font-size: 14px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        /* Single column layout for property description */
        .property-description-content {
          grid-template-columns: 1fr;
          gap: 12px;
        }

        /* Modern field styling */
        .property-field {
          display: flex;
          align-items: center;
          margin-bottom: var(--space-3);
          padding: var(--space-3) var(--space-4);
          border-radius: var(--radius-md);
          border: 1px solid var(--gray-200);
          background: linear-gradient(135deg, white 0%, var(--gray-50) 100%);
          transition: all 0.2s ease;
          box-shadow: var(--shadow-xs);
          backdrop-filter: blur(5px);
          min-height: 48px;
        }

        .property-field:hover {
          background: linear-gradient(135deg, white 0%, var(--primary-50) 100%);
          border-color: var(--primary-200);
          transform: translateY(-1px);
          box-shadow: var(--shadow-md);
        }

        .property-label {
          font-weight: 600;
          color: var(--primary-700);
          font-size: var(--text-sm);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          width: 140px;
          font-family: var(--font-sans);
          line-height: 1.4;
          margin-right: var(--space-4);
        }

        .property-value {
          color: var(--gray-800);
          font-size: var(--text-base);
          font-weight: 500;
          flex: 1;
          font-family: var(--font-sans);
          line-height: 1.4;
          word-break: break-word;
          overflow-wrap: break-word;
          max-width: 100%;
        }

        /* Remove old column classes - now using grid */
        .property-left-column,
        .property-right-column {
          display: contents;
        }

        /* Tables - Modern Design System */
        .modern-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          background: white;
          margin-bottom: var(--space-4);
          border: 1px solid var(--primary-200);
          border-radius: var(--radius-lg);
          overflow: hidden;
          box-shadow: var(--shadow-md);
        }

        .modern-table thead th {
          background: linear-gradient(135deg, var(--primary-600) 0%, var(--primary-700) 100%);
          color: white;
          padding: var(--space-3);
          text-align: left;
          font-weight: 600;
          font-size: var(--text-sm);
          border: none;
          vertical-align: middle;
          font-family: var(--font-sans);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          position: relative;
        }

        .modern-table thead th::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 50%, rgba(0, 0, 0, 0.05) 100%);
          pointer-events: none;
        }

        .modern-table tbody td {
          padding: var(--space-3);
          border-bottom: 1px solid var(--gray-100);
          font-size: var(--text-sm);
          color: var(--gray-800);
          vertical-align: middle;
          font-family: var(--font-sans);
          transition: background-color 0.2s ease;
        }

        .modern-table tbody tr {
          background: white;
        }

        .modern-table tbody tr:nth-child(even) {
          background: var(--gray-50);
        }

        .modern-table tbody tr:hover {
          background: var(--primary-50);
        }

        .modern-table tbody tr:last-child td {
          border-bottom: none;
        }

        /* Column-specific styling - Modern Design System */
        .modern-table .category-cell {
          font-weight: 700;
          text-align: left;
          color: var(--primary-700);
          width: 20%;
        }

        .modern-table .quantity-cell {
          text-align: center;
          font-weight: 600;
          color: var(--gray-900);
          background: var(--gray-50);
          border-radius: var(--radius-sm);
          width: 10%;
        }

        .modern-table .condition-cell {
          font-weight: 600;
          text-align: left;
          width: 15%;
        }

        .modern-table .notes-cell {
          text-align: left;
          color: var(--gray-700);
          width: 55%;
        }

        .modern-table .description-cell {
          text-align: left;
          line-height: 1.4;
        }

        /* Photo Grid - Modern Design System */
        .photos-section {
          margin-top: var(--space-4);
        }

        .photo-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 10px 12px;
          margin-bottom: 12px;
          max-width: 100%;
          page-break-inside: avoid;
          page-break-after: always;
        }

        .photo-container {
          border: 1px solid var(--primary-200);
          border-radius: var(--radius-lg);
          overflow: hidden;
          background: white;
          padding: 6px;
          box-shadow: var(--shadow-md);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .photo-container:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-lg);
          border-color: var(--primary-300);
        }

        .photo-header {
          background: #f8f9fa;
          border: 1px solid #e5e7eb;
          padding: 12px 16px;
          margin-bottom: 8px;
          border-radius: 6px;
          font-family: var(--font-sans);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .photo-header-category {
          font-size: 14px;
          font-weight: 700;
          color: #374151;
          margin-bottom: 4px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .photo-header-details {
          font-size: 12px;
          color: #6b7280;
          line-height: 1.4;
        }

        .photo-header-condition {
          font-weight: 600;
          margin-bottom: 2px;
        }

        .photo-header-notes {
          font-style: italic;
          color: #9ca3af;
        }

        .photo-image {
          width: 100%;
          height: 400px;
          min-height: 380px;
          max-height: 420px;
          object-fit: cover;
          display: block;
          border-radius: var(--radius-md);
          image-rendering: high-quality;
          image-rendering: -webkit-optimize-contrast;
        }

        .photo-number {
          position: absolute;
          top: var(--space-2);
          left: var(--space-2);
          background: linear-gradient(135deg, var(--primary-500) 0%, var(--primary-600) 100%);
          color: white;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: var(--text-xs);
          font-weight: 700;
          box-shadow: var(--shadow-sm);
        }

        .photo-wrapper {
          position: relative;
        }

        .photo-empty-slot {
          min-height: 100px;
          visibility: hidden;
        }

        .page-break-after-grid {
          page-break-after: always !important;
          break-after: page !important;
        }

        /* Glossary - Modern Design System */
        .glossary-title {
          font-size: var(--text-2xl);
          font-weight: 700;
          color: var(--primary-700);
          margin: 0 0 var(--space-6) 0;
          position: relative;
          display: inline-block;
          font-family: var(--font-sans);
          letter-spacing: 0.02em;
          word-spacing: 0.15em;
        }
        
        .glossary-title::after {
          content: '';
          position: absolute;
          bottom: calc(var(--space-3) * -1);
          left: 0;
          width: 100%;
          height: 2px;
          background: linear-gradient(90deg, var(--primary-500), var(--primary-300));
          border-radius: var(--radius-sm);
        }

        .glossary-category {
          background: linear-gradient(135deg, var(--gray-100) 0%, var(--gray-200) 100%);
          border: 1px solid var(--gray-200);
          border-radius: var(--radius-lg);
          padding: var(--space-3) var(--space-4);
          margin: 0 0 var(--space-3) 0;
          font-size: var(--text-base);
          font-weight: 600;
          color: var(--primary-700);
          min-height: 40px;
          display: flex;
          align-items: center;
          font-family: var(--font-sans);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          box-shadow: var(--shadow-sm);
        }

        .glossary-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: var(--space-3) var(--space-6);
          margin-bottom: var(--space-4);
          align-items: start;
        }

        .glossary-item {
          background: linear-gradient(135deg, white 0%, var(--primary-50) 100%);
          border: 1px solid var(--primary-100);
          border-radius: var(--radius-lg);
          padding: var(--space-2);
          margin: 0;
          break-inside: avoid;
          box-shadow: var(--shadow-md);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .glossary-item:hover {
          transform: translateY(-1px);
          box-shadow: var(--shadow-lg);
          border-color: var(--primary-200);
        }

        .glossary-term {
          font-weight: 700;
          color: var(--primary-700);
          font-size: var(--text-sm);
          margin-bottom: var(--space-2);
          line-height: 1.3;

.glossary-definition {
font-size: var(--text-xs);
color: var(--gray-700);
line-height: 1.4;
margin-top: 0;
text-align: left;

        /* Signature Disclaimer */
        .signature-disclaimer {
          margin: 20px 0;
          padding: 15px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        }

        .signature-disclaimer-text {
          font-size: var(--text-sm);
          line-height: 1.5;
          color: var(--gray-700);
          text-align: justify;
        }

        /* Signatures Section - Professional Design matching jsPDF */
        .signatures-section {
          margin-top: 25px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 30px;
          max-width: 500px;
          margin-left: auto;
          margin-right: auto;
        }

        .signature-box {
          text-align: center;
          padding: 15px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
}

.signature-image {
  width: 100%;
  height: 108px;
  border: none;
  border-bottom: 1px solid #374151;
  border-radius: 0;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  overflow: hidden;
}

.signature-image img {
  max-height: 108px;
  max-width: 100%;
  object-fit: contain;
}

.signature-label {
  font-size: 18px !important;
  color: #000000 !important;
  font-weight: 900 !important;
  font-family: var(--font-sans) !important;
  margin-bottom: 10px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.1em !important;
  text-shadow: 0 2px 4px rgba(0,0,0,0.8) !important;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%) !important;
  padding: 10px 15px !important;
  border-radius: 6px !important;
  border: 3px solid #000000 !important;
  box-shadow: 0 4px 8px rgba(0,0,0,0.3) !important;
  display: block !important;
  text-align: center !important;
  width: 100% !important;
  box-sizing: border-box !important;
}

.signature-date {
  font-size: var(--text-sm);
  color: var(--gray-600);
  margin-top: 10px;
  font-family: var(--font-sans);
}

/* Disclaimer and Contact Sections */
.disclaimer-section {
  margin-top: 40px;
  padding: 20px;
  background: #fff8f0;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
          font-weight: 600;
          font-family: var(--font-sans);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        /* Table of Contents Styles */
        .contents-section {
          background: linear-gradient(135deg, white 0%, var(--gray-50) 100%);
          border: 1px solid var(--primary-200);
          border-radius: var(--radius-xl);
          padding: 12px 16px;
          margin-bottom: 12px;
          box-shadow: var(--shadow-lg);
        }

        .contents-list {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .contents-item {
          border-bottom: 1px solid var(--gray-200);
          padding-bottom: 4px;
        }

        .contents-item:last-child {
          border-bottom: none;
        }

        .contents-link {
          display: flex;
          align-items: baseline;
          text-decoration: none;
          color: inherit;
          padding: 4px 0;
          transition: all 0.2s ease;
          position: relative;
        }

        .contents-link:hover {
          background: var(--primary-50);
          padding-left: var(--space-3);
          padding-right: var(--space-3);
          border-radius: var(--radius-md);
        }

        .contents-link:hover .contents-title {
          color: var(--primary-700);
          font-weight: 600;
        }

        .contents-link:hover .contents-page {
          color: var(--primary-600);
          font-weight: 600;
        }

        .contents-title {
          font-size: var(--text-base);
          font-weight: 500;
          color: var(--gray-800);
          transition: all 0.2s ease;
          flex-shrink: 0;
        }

        .contents-dots {
          flex-grow: 1;
          height: 1px;
          background: repeating-linear-gradient(
            to right,
            var(--gray-400) 0px,
            var(--gray-400) 3px,
            transparent 3px,
            transparent 8px
          );
          margin: 0 var(--space-3);
          align-self: center;
        }

        .contents-page {
          font-size: var(--text-base);
          font-weight: 600;
          color: var(--primary-600);
          background: var(--primary-100);
          padding: var(--space-1) var(--space-2);
          border-radius: var(--radius-sm);
          min-width: 32px;
          text-align: center;
          transition: all 0.2s ease;
          flex-shrink: 0;
        }

        /* Disclaimer and Contact Sections */
        .disclaimer-section {
          margin-top: 40px;
          padding: 20px;
          background: #fff8f0;
          border: 1px solid #fed7aa;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .disclaimer-title {
          font-size: 14px;
          font-weight: 700;
          color: #ea580c;
          margin-bottom: 12px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .disclaimer-text {
          font-size: 11px;
          line-height: 1.5;
          color: #7c2d12;
          text-align: justify;
        }

        .contact-section {
          margin-top: 30px;
          padding: 20px;
          background: #f8fafc;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .contact-title {
          font-size: 16px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 15px;
          text-align: center;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .contact-details {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 15px;
          font-size: 12px;
          line-height: 1.6;
          color: #334155;
        }

        .contact-item {
          display: flex;
          align-items: flex-start;
          gap: 8px;
        }

        .contact-label {
          font-weight: 600;
          color: #475569;
          min-width: 60px;
        }

        .contact-value {
          color: #1e293b;
        }

        /* Utility Classes */
        .text-center { text-align: center; }
        .text-right { text-align: right; }
        .font-bold { font-weight: bold; }
        .mb-2 { margin-bottom: 4px; }
        .mb-4 { margin-bottom: 8px; }
        .mt-2 { margin-top: 4px; }
        .mt-4 { margin-top: 8px; }

        /* Page Numbers */
        .page-number {
          position: fixed;
          top: 10mm;
          right: 15mm;
          font-size: 8pt;
          color: #64748b;
        }

        /* Room Headers - EXACT match to jsPDF room headers */
        .room-header {
          background: var(--primary-500);
          color: white;
          padding: 12px 8px; /* increased padding for better visibility */
          border-radius: 4px; /* slightly larger radius */
          margin: 15px 0 15px 0; /* increased margins for proper spacing */
          font-weight: bold;
          font-size: 14pt; /* slightly larger font */
          min-height: 20px; /* minimum height instead of fixed */
          line-height: 1.2;
          display: flex;
          align-items: center;
          position: relative;
          z-index: 10; /* ensure it stays above other elements */
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        /* No-break containers */
        .inventory-table-container {
          page-break-inside: avoid;
          margin-bottom: 8px;
        }

        /* Contents Page Styles */
        .contents-section {
          margin-top: 8px;
        }

        .contents-list {
          display: flex;
          flex-direction: column;
          gap: 6px;
          max-width: 100%;
          margin: 0 auto;
        }

        .contents-item {
          border-bottom: 1px solid var(--gray-200);
          padding: 6px 0;
          transition: background-color 0.2s ease;
        }

        .contents-item:hover {
          background-color: var(--primary-50);
          border-radius: var(--radius-md);
          padding: var(--space-3);
        }

        .contents-link {
          display: flex;
          align-items: center;
          text-decoration: none;
          color: var(--gray-800);
          cursor: pointer;
          padding: var(--space-2) var(--space-3);
          border-radius: var(--radius-md);
          transition: all 0.2s ease;
        }

        .contents-link:hover {
          color: var(--primary-700);
          background-color: var(--primary-50);
          transform: translateX(4px);
        }

        .contents-title {
          font-weight: 600;
          font-size: var(--text-base);
          color: inherit;
          min-width: 0;
          flex-shrink: 1;
        }

        .contents-dots {
          flex-grow: 1;
          height: 1px;
          background: repeating-linear-gradient(
            to right,
            var(--gray-400) 0,
            var(--gray-400) 4px,
            transparent 4px,
            transparent 8px
          );
          margin: 0 var(--space-3);
          min-width: 40px;
        }

        .contents-page {
          font-weight: 700;
          color: var(--primary-600);
          background: var(--primary-100);
          padding: var(--space-1) var(--space-2);
          border-radius: var(--radius-md);
          min-width: 32px;
          text-align: center;
          flex-shrink: 0;
        }

        .contents-link:hover .contents-page {
          background: var(--primary-200);
          color: var(--primary-800);
        }

        /* Print-specific styles for contents */
        @media print {
          .contents-link {
            color: #333 !important;
          }
          
          .contents-link:hover {
            transform: none;
            background-color: transparent;
          }
        }

        /* Photo Click-to-Enlarge Styles */
        .photo-grid img {
          cursor: pointer;
          transition: opacity 0.2s ease;
          -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
          touch-action: manipulation;
          user-select: none;
          -webkit-user-select: none;
        }

        .photo-grid img:hover,
        .photo-grid img:active {
          opacity: 0.9;
        }

        /* Lightbox Modal Styles */
        .lightbox-modal {
          display: none;
          position: fixed;
          z-index: 10000;
          left: 0;
          top: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.95);
          animation: fadeIn 0.2s ease;
        }

        .lightbox-modal.active {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .lightbox-content {
          max-width: 95%;
          max-height: 95%;
          object-fit: contain;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
          animation: zoomIn 0.2s ease;
        }

        .lightbox-close {
          position: absolute;
          top: 20px;
          right: 35px;
          color: white;
          font-size: 40px;
          font-weight: bold;
          cursor: pointer;
          transition: color 0.2s ease;
          z-index: 10001;
        }

        .lightbox-close:hover {
          color: #f87171;
        }

        .lightbox-caption {
          position: absolute;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          color: white;
          font-size: 14pt;
          padding: 10px 20px;
          background: rgba(0, 0, 0, 0.7);
          border-radius: 6pt;
          max-width: 80%;
          text-align: center;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes zoomIn {
          from { transform: scale(0.8); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* Download PDF Button */
        .download-pdf-button {
          position: fixed;
          bottom: 30px;
          right: 30px;
          z-index: 9999;
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
          border: none;
          padding: 14px 24px;
          border-radius: 50px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
          display: flex;
          align-items: center;
          gap: 10px;
          transition: all 0.3s ease;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .download-pdf-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(59, 130, 246, 0.5);
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
        }

        .download-pdf-button:active {
          transform: translateY(0);
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
        }

        .download-pdf-button svg {
          flex-shrink: 0;
        }

        .download-pdf-button.generating {
          opacity: 0.7;
          cursor: wait;
        }

        .download-pdf-button.generating::after {
          content: '';
          position: absolute;
          width: 20px;
          height: 20px;
          top: 50%;
          left: 50%;
          margin-left: -10px;
          margin-top: -10px;
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top-color: white;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Hide button during print */
        @media print {
          .download-pdf-button {
            display: none;
          }
        }
      </style>
    `;
  }

  /**
   * Generate header section - Address as main title
   */
  private static generateHeaderSection(
    propertyDetails: PropertyInventoryData['propertyDetails'], 
    pageNumber: number = 1, 
    totalPages: number = 12, 
    logoDataUrl?: string,
    companyName: string = 'Energy Ltd',
    companyWebsite: string = 'www.example.com',
    companyPhone: string = '0800 123 4567'
  ): string {
    const reportTypeText = propertyDetails.reportType === 'checkIn' ? 'Check-in' : propertyDetails.reportType === 'midTerm' ? 'Mid-Term Report' : 'Check-out';
    
    // Use custom logo if provided, otherwise use default logo
    const logoSvg = logoDataUrl || `data:image/svg+xml;base64,${btoa(`
      <svg width="40" height="15" viewBox="0 0 40 15" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="2" width="6" height="6" fill="#00796b" rx="1"/>
        <rect x="2" y="9" width="6" height="4" fill="#009688" rx="1"/>
        <rect x="9" y="5" width="6" height="8" fill="#00796b" rx="1"/>
        <text x="17" y="8" font-family="Arial, sans-serif" font-size="6" font-weight="bold" fill="#00796b">ClearGrade</text>
        <text x="17" y="13" font-family="Arial, sans-serif" font-size="4" fill="#00796b">Energy</text>
      </svg>
    `)}`;
    
    return `
      <header class="header">
        <div class="header-content">
          <div class="header-left">
            <img src="${logoSvg}" alt="Company Logo" class="header-logo" />
            <div class="header-text">
              <div class="header-title">${propertyDetails.address || 'Property Address'}</div>
              <div class="report-type">${reportTypeText} Report</div>
              <div class="report-subtitle">Property Inventory</div>
            </div>
          </div>
          ${(companyName || companyWebsite) ? `
          <div class="header-right">
            ${companyName ? `<div class="company-name">${companyName}</div>` : ''}
            ${companyWebsite ? `<div class="company-website">${companyWebsite}</div>` : ''}
            ${companyPhone ? `<div class="company-phone">${companyPhone}</div>` : ''}
          </div>
          ` : ''}
          <div class="header-page">Page ${pageNumber} of ${totalPages}</div>
        </div>
        <div class="header-line"></div>
      </header>
    `;
  }

  /**
   * Capitalize words helper
   */
  private static capitalizeWords(str: string): string {
    return str.replace(/\b\w/g, char => char.toUpperCase());
  }

  /**
   * Generate property details section - Pixel-perfect match to jsPDF positioning
   */
  private static generatePropertyDetailsSection(propertyDetails: PropertyInventoryData['propertyDetails']): string {
    const reportTypeText = propertyDetails.reportType === 'checkIn' ? 'Check-in' : 
                          propertyDetails.reportType === 'midTerm' ? 'Mid-Term Inspection' : 'Check-out';
    const checkDateField = propertyDetails.reportType === 'checkIn' ? 'Check-in Date' : 
                          propertyDetails.reportType === 'midTerm' ? 'Inspection Date' : 'Check-out Date';
    const checkDateValue = propertyDetails.reportType === 'checkIn' ? 
      (propertyDetails.checkInDate || 'N/A') : 
      propertyDetails.reportType === 'midTerm' ? (propertyDetails.checkInDate || 'N/A') : 
      (propertyDetails.checkOutDate || 'N/A');
    
    return `
      <section class="property-details-section">
        <div class="section-title">Property Details</div>
        <div class="property-details-content">
          <div class="property-field">
            <span class="property-label">Address</span>
            <span class="property-value">${propertyDetails.address || 'N/A'}</span>
          </div>
          <div class="property-field">
            <span class="property-label">Inspected By</span>
            <span class="property-value">${propertyDetails.agent || 'N/A'}</span>
          </div>
          <div class="property-field">
            <span class="property-label">Inspection Date</span>
            <span class="property-value">${propertyDetails.reportDate || 'N/A'}</span>
          </div>
          <div class="property-field">
            <span class="property-label">Landlord</span>
            <span class="property-value">${propertyDetails.landlordName || 'N/A'}</span>
          </div>
          <div class="property-field">
            <span class="property-label">${checkDateField}</span>
            <span class="property-value">${checkDateValue}</span>
          </div>
        </div>
      </section>
      
      ${this.generateTenantsSection(propertyDetails)}
      
      ${propertyDetails.propertyType ? `
      <section class="property-description-section">
        <div class="section-title">Property Description</div>
        <div class="property-description-content">
          <div class="property-field">
            <span class="property-label">${this.capitalizeWords('Property Type')}</span>
            <span class="property-value">${this.capitalizeWords(propertyDetails.propertyType)}</span>
          </div>
          <div class="property-field">
            <span class="property-label">${this.capitalizeWords('Bedrooms')}</span>
            <span class="property-value">${(propertyDetails as any).bedrooms || 'N/A'}</span>
          </div>
          <div class="property-field">
            <span class="property-label">${this.capitalizeWords('Cleanliness Standard')}</span>
            <span class="property-value">${(propertyDetails as any).cleanliness ? this.capitalizeWords((propertyDetails as any).cleanliness) : 'N/A'}</span>
          </div>
          ${(propertyDetails as any).tenantsPresent !== undefined ? `
          <div class="property-field">
            <span class="property-label">${this.capitalizeWords('Tenants Present at Inspection')}</span>
            <span class="property-value" style="font-weight: 600; color: ${(propertyDetails as any).tenantsPresent ? '#10b981' : '#f59e0b'};">
              ${(propertyDetails as any).tenantsPresent ? '✓ Yes' : '✗ No'}
            </span>
          </div>
          ` : ''}
        </div>
      </section>` : ''}
      
      <!-- Photo Enlargement Notice -->
      <div style="background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); color: white; padding: 12px 16px; border-radius: 8px; margin-top: 16px; border-left: 4px solid #1e40af; box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);">
        <div style="display: flex; align-items: center; gap: 12px;">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex-shrink: 0;">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <circle cx="8.5" cy="8.5" r="1.5"></circle>
            <polyline points="21 15 16 10 5 21"></polyline>
          </svg>
          <div>
            <div style="font-weight: 600; margin-bottom: 4px;">📱 View Photos in Full Size</div>
            <div style="font-size: 14px; opacity: 0.95;">Open this HTML file on a desktop or laptop to click any photo and view it enlarged. This feature is not available in the static PDF version.</div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Generate tenants section as a separate table
   */
  private static generateTenantsSection(propertyDetails: PropertyInventoryData['propertyDetails']): string {
    // Handle multiple tenant names - comprehensive parsing
    const tenantName = propertyDetails.tenantName || '';
    let tenantNames: string[] = [];
    
    // Debug logging
    console.log('🏠 [TENANT DEBUG] Raw tenant data:', JSON.stringify(tenantName));
    console.log('🏠 [TENANT DEBUG] Tenant name length:', tenantName.length);
    
    if (tenantName.trim()) {
      // First try common separators
      let splitAttempt = tenantName.split(/[,;\n|&]/);
      console.log('🏠 [TENANT DEBUG] Split by separators:', splitAttempt);
      
      if (splitAttempt.length > 1) {
        tenantNames = splitAttempt.map(name => name.trim()).filter(name => name.length > 0);
        console.log('🏠 [TENANT DEBUG] Found multiple tenants via separators:', tenantNames);
      } else {
        // Try "and" separator (case insensitive)
        splitAttempt = tenantName.split(/\s+and\s+/i);
        console.log('🏠 [TENANT DEBUG] Split by "and":', splitAttempt);
        if (splitAttempt.length > 1) {
          tenantNames = splitAttempt.map(name => name.trim()).filter(name => name.length > 0);
          console.log('🏠 [TENANT DEBUG] Found multiple tenants via "and":', tenantNames);
        } else {
          // Try double space or tab separators
          splitAttempt = tenantName.split(/\s{2,}|\t/);
          console.log('🏠 [TENANT DEBUG] Split by spaces/tabs:', splitAttempt);
          if (splitAttempt.length > 1) {
            tenantNames = splitAttempt.map(name => name.trim()).filter(name => name.length > 0);
            console.log('🏠 [TENANT DEBUG] Found multiple tenants via spaces:', tenantNames);
          } else {
            // Single tenant
            tenantNames = [tenantName.trim()];
            console.log('🏠 [TENANT DEBUG] Single tenant:', tenantNames);
          }
        }
      }
    }

    if (tenantNames.length === 0) {
      tenantNames.push('N/A');
      console.log('🏠 [TENANT DEBUG] No tenant data, using N/A');
    }
    
    console.log('🏠 [TENANT DEBUG] Final tenant names array:', tenantNames);

    // Debug: Add tenant count info to help identify issues
    const debugInfo = tenantNames.length > 1 ? ` (${tenantNames.length} tenants found)` : '';
    
    const tenantRows = tenantNames.map((name, index) => `
      <tr>
        <td class="category-cell">Tenant ${index + 1}</td>
        <td class="description-cell">${name}</td>
      </tr>
    `).join('');

    return `
      <section class="property-details-section">
        <div class="section-title">Tenant Information</div>
        <table class="modern-table">
          <thead>
            <tr>
              <th>Tenant</th>
              <th>Name</th>
            </tr>
          </thead>
          <tbody>
            ${tenantRows}
          </tbody>
        </table>
      </section>
    `;
  }

  /**
   * Generate glossary section - EXACT match to jsPDF layout
   */
  private static generateGlossarySection(glossaryTerms: Record<string, string>): string {
    // Separate condition and cleanliness descriptions
    const conditionTerms: Record<string, string> = {};
    const cleanlinessTerms: Record<string, string> = {};
    
    // Known condition terms from jsPDF constants
    const conditionKeys = ['Brand new', 'Good', 'Fair', 'Poor', 'Very Poor'];
    const cleanlinessKeys = ['Professionally Cleaned', 'Cleaned to professional standard', 'Good domestic standard', 'Average Domestic standard', 'Poor domestic standard', 'Not cleaned'];
    
    Object.entries(glossaryTerms).forEach(([term, definition]) => {
      // Check cleanliness keys first since they're more specific (e.g., "Poor domestic standard")
      if (cleanlinessKeys.some(key => term.toLowerCase().includes(key.toLowerCase()))) {
        cleanlinessTerms[term] = definition;
      } else if (conditionKeys.some(key => term.toLowerCase().includes(key.toLowerCase()))) {
        conditionTerms[term] = definition;
      } else {
        // Default to condition if unsure
        conditionTerms[term] = definition;
      }
    });

    const generateCategorySection = (title: string, terms: Record<string, string>) => {
      if (Object.keys(terms).length === 0) return '';
      
      const termsHTML = Object.entries(terms)
        .map(([term, definition]) => `<div class="glossary-item"><div class="glossary-term">${term}</div><div class="glossary-definition">${definition}</div></div>`)
        .join('');

      return `
        <div class="glossary-category">${title}</div>
        <div class="glossary-grid">
          ${termsHTML}
        </div>
      `;
    };

    return `
      <div class="glossary-title">Glossary of Terms</div>
      ${generateCategorySection('Condition Descriptions', conditionTerms)}
      ${generateCategorySection('Cleanliness Descriptions', cleanlinessTerms)}
    `;
  }

  /**
   * Generate certificates, keys, and meter readings section
   */
  private static async generateCertificatesSection(
    certificates?: any,
    keys?: Array<any>,
    meterReadings?: any
  ): Promise<string> {
    let content = '';

    // Gas Certificate
    if (certificates?.gas && (certificates.gas.date || certificates.gas.number)) {
      content += `
        <div class="subsection-header">Gas Safety Certificate</div>
        <div class="inventory-table-container">
          <table class="modern-table">
            <thead>
              <tr>
                <th>Expiry Date</th>
                <th>Certificate Number</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>${certificates.gas.date || 'N/A'}</td>
                <td>${certificates.gas.number || 'N/A'}</td>
              </tr>
            </tbody>
          </table>
        </div>
      `;
    }

    // EICR Certificate
    if (certificates?.electrical && (certificates.electrical.date || certificates.electrical.number)) {
      content += `
        <div class="subsection-header">Electrical Safety Certificate (EICR)</div>
        <div class="inventory-table-container">
          <table class="modern-table">
            <thead>
              <tr>
                <th>Expiry Date</th>
                <th>Certificate Number</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>${certificates.electrical.date || 'N/A'}</td>
                <td>${certificates.electrical.number || 'N/A'}</td>
              </tr>
            </tbody>
          </table>
        </div>
      `;
    }

    if (keys && keys.length > 0) {
      content += `
        <div class="subsection-header">Keys</div>
        <div class="inventory-table-container">
          <table class="modern-table">
            <thead>
              <tr>
                <th>Handed Over</th>
                <th>Type</th>
                <th>Quantity</th>
              </tr>
            </thead>
            <tbody>
              ${keys.map(key => `
                <tr>
                  <td>${key.handedOver}</td>
                  <td>${key.type}</td>
                  <td class="quantity-cell">${key.quantity}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
    }

    // Meter Readings
    const meterTypes = ['gas', 'electricity', 'water'];
    for (const meterType of meterTypes) {
      const meter = meterReadings?.[meterType];
      if (meter && (meter.reading || meter.location || meter.serialNo)) {
        const meterLabel = meterType.charAt(0).toUpperCase() + meterType.slice(1);
        content += `
          <div class="subsection-header">${meterLabel} Meter</div>
          <div class="inventory-table-container">
            <table class="modern-table">
              <thead>
                <tr>
                  <th>Location</th>
                  <th>Serial Number</th>
                  <th>Reading</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>${meter.location || 'N/A'}</td>
                  <td>${meter.serialNo || 'N/A'}</td>
                  <td class="quantity-cell">${meter.reading || 'N/A'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        `;
      }
    }

    return content;
  }

  /**
   * Generate certificate/keys/meter photos section with pagination (4 photos per page)
   */
  private static async generateCertificatePhotosSection(
    certificates?: any,
    keys?: Array<any>,
    meterReadings?: any,
    propertyDetails?: PropertyInventoryData['propertyDetails'],
    startPage?: number,
    totalPages?: number,
    logoDataUrl?: string,
    companyName?: string,
    companyWebsite?: string,
    companyPhone?: string
  ): Promise<string> {
    let currentPage = startPage || 1;
    let photosHTML = '';
    
    // Collect all photos from all sources
    const allPhotos: Array<{ photo: any, label: string, category: string }> = [];
    
    // Gas certificate photos
    if (certificates?.gas?.photos?.length > 0) {
      const validPhotos = certificates.gas.photos.filter((p: any) => p && !p.uploading && (p.url || p.localPath));
      validPhotos.forEach((photo: any) => {
        allPhotos.push({ photo, label: 'Gas Safety Certificate', category: 'Certificates' });
      });
    }
    
    // EICR certificate photos
    if (certificates?.electrical?.photos?.length > 0) {
      const validPhotos = certificates.electrical.photos.filter((p: any) => p && !p.uploading && (p.url || p.localPath));
      validPhotos.forEach((photo: any) => {
        allPhotos.push({ photo, label: 'Electrical Safety Certificate (EICR)', category: 'Certificates' });
      });
    }
    
    // Keys photos
    if (keys && Array.isArray(keys)) {
      keys.forEach((key: any, index: number) => {
        if (key.photos?.length > 0) {
          const validPhotos = key.photos.filter((p: any) => p && !p.uploading && (p.url || p.localPath));
          validPhotos.forEach((photo: any) => {
            allPhotos.push({ 
              photo, 
              label: `Keys - ${key.handedOver || `Key ${index + 1}`}`,
              category: 'Keys'
            });
          });
        }
      });
    }
    
    // Meter readings photos
    ['gas', 'electricity', 'water'].forEach(meterType => {
      const meter = meterReadings?.[meterType];
      if (meter?.photos?.length > 0) {
        const validPhotos = meter.photos.filter((p: any) => p && !p.uploading && (p.url || p.localPath));
        const meterLabel = meterType.charAt(0).toUpperCase() + meterType.slice(1);
        validPhotos.forEach((photo: any) => {
          allPhotos.push({ 
            photo, 
            label: `${meterLabel} Meter`,
            category: 'Meter Readings'
          });
        });
      }
    });
    
    // Generate pages with 4 photos each (2x2 grid)
    if (allPhotos.length > 0) {
      for (let i = 0; i < allPhotos.length; i += 4) {
        const photoBatch = allPhotos.slice(i, i + 4);
        const isFirstBatch = (i === 0);
        
        photosHTML += `
          <section class="pdf-page" id="${isFirstBatch ? 'certificate-photos' : 'certificate-photos-' + Math.floor(i / 4)}">
            ${this.generateHeaderSection(propertyDetails!, currentPage++, totalPages!, logoDataUrl, companyName, companyWebsite, companyPhone)}
            <div class="section-header">Certificates, Keys & Meter Photos</div>
            <div class="photo-grid">
        `;
        
        for (const [index, photoData] of photoBatch.entries()) {
          const photoNumber = i + index + 1;
          const imageUrl = await this.getPhotoUrl(photoData.photo);
          
          photosHTML += `
            <div class="photo-container">
              <div class="photo-wrapper">
                <img src="${imageUrl}" alt="${photoData.label} Photo ${photoNumber}" class="photo-image" />
                <div class="photo-number">${photoNumber}</div>
              </div>
              <div class="photo-caption">${photoData.label}</div>
            </div>
          `;
        }
        
        // Fill empty slots if batch has less than 4 photos
        for (let j = photoBatch.length; j < 4; j++) {
          photosHTML += '<div class="photo-empty-slot"></div>';
        }
        
        photosHTML += `
            </div>
          </section>
        `;
      }
    }
    
    return photosHTML;
  }

  /**
   * Generate room inventory section with headers on each page
   * Splits inventory items into multiple pages if more than 15 items
   */
  private static async generateRoomInventorySection(
    rooms: Room[], 
    propertyDetails: PropertyInventoryData['propertyDetails'], 
    startPage: number, 
    totalPages: number, 
    logoDataUrl?: string,
    companyName?: string,
    companyWebsite?: string,
    companyPhone?: string
  ): Promise<string> {
    let currentPage = startPage;
    const ITEMS_PER_PAGE = 15; // Maximum items per inventory page
    const isMidTerm = propertyDetails.reportType === 'midTerm';
    
    const roomHTMLPromises = rooms.map(async (room, index) => {
      // Mid-Term Report: Show rating, cleanliness, notes with photos
      if (isMidTerm) {
        const rating = (room as any).rating;
        const cleanliness = (room as any).cleanliness;
        // Notes can be at room level or in first item
        const notes = (room as any).notes || room.items?.[0]?.notes || '';
        
        console.log(`🏠 [MID-TERM ROOM ${index}] ${room.name}:`, {
          rating,
          cleanliness,
          notes: notes ? 'YES' : 'NO',
          photoCount: room.items?.[0]?.photos?.length || 0
        });
        const photos = room.items?.[0]?.photos?.filter((p: RoomPhoto) => 
          p && !p.uploading && (p.url || p.localPath)
        ) || [];
        
        const ratingText = rating === 1 ? '⭐ Excellent' :
                          rating === 2 ? '✨ Good' :
                          rating === 3 ? '👍 Average' :
                          rating === 4 ? '⚠️ Below Average' :
                          rating === 5 ? '❌ Poor' : 'Not Rated';
        
        const ratingColor = rating === 1 ? '#10b981' :
                           rating === 2 ? '#22c55e' :
                           rating === 3 ? '#f59e0b' :
                           rating === 4 ? '#f97316' :
                           rating === 5 ? '#ef4444' : '#6b7280';
        
        // Generate room pages with photos - fit up to 4 photos per page
        const PHOTOS_PER_PAGE = 4;
        const roomPages: string[] = [];
        let photoPages = 0;
        
        if (photos.length > 0) {
          photoPages = Math.ceil(photos.length / PHOTOS_PER_PAGE);
        }
        
        // First page: Room info + up to 4 photos
        let firstPageHTML = `
          <section class="pdf-page" id="room-${index}">
            ${this.generateHeaderSection(propertyDetails, currentPage++, totalPages, logoDataUrl, companyName, companyWebsite, companyPhone)}
            <div class="room-header" style="background: var(--primary-500) !important;">${room.name || 'Unnamed Room'}</div>
            
            <!-- Compact Room Summary Card -->
            ${(rating !== undefined && rating !== null) || cleanliness || notes ? `
            <div style="background: white; border: 1px solid #e5e7eb; border-radius: 8px; padding: 16px; margin-top: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
              <div style="display: grid; grid-template-columns: ${notes ? '1fr 1fr' : '1fr 1fr'}; gap: 16px; ${notes ? 'margin-bottom: 16px;' : ''}">
                ${rating !== undefined && rating !== null ? `
                <div>
                  <div style="font-size: 11px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px;">Condition Rating</div>
                  <div style="display: flex; align-items: center; gap: 8px;">
                    <div style="font-size: 20px; font-weight: 700; color: ${ratingColor};">${rating}/5</div>
                    <div style="font-size: 14px; color: #374151;">${ratingText}</div>
                  </div>
                </div>
                ` : ''}
                ${cleanliness ? `
                <div>
                  <div style="font-size: 11px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px;">Cleanliness</div>
                  <div style="font-size: 14px; font-weight: 600; color: #1e293b;">
                    ${this.capitalizeWords(cleanliness)}
                  </div>
                </div>
                ` : ''}
              </div>
              ${notes ? `
              <div style="border-top: 1px solid #e5e7eb; padding-top: 12px;">
                <div style="font-size: 11px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px;">Notes</div>
                <div style="font-size: 13px; color: #374151; line-height: 1.5; white-space: pre-wrap;">
                  ${notes}
                </div>
              </div>
              ` : ''}
            </div>
            ` : ''}
        `;
        
        // Add photos to first page if they exist
        if (photos.length > 0) {
          const firstPagePhotos = photos.slice(0, PHOTOS_PER_PAGE);
          firstPageHTML += `
            <div style="margin-top: 24px;">
              <div style="font-size: 16px; font-weight: 600; color: #1e293b; margin-bottom: 12px;">Room Photos</div>
              <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px;">
          `;
          
          for (let i = 0; i < firstPagePhotos.length; i++) {
            const photo = firstPagePhotos[i];
            const imageUrl = await this.getPhotoUrl(photo);
            const photoTimestamp = photo.timestamp ? new Date(photo.timestamp).toLocaleString('en-GB', { 
              day: '2-digit', 
              month: '2-digit', 
              year: 'numeric', 
              hour: '2-digit', 
              minute: '2-digit' 
            }) : '';
            firstPageHTML += `
              <div style="position: relative; padding-top: 75%; background: #f3f4f6; border-radius: 8px; overflow: hidden;">
                <img src="${imageUrl}" alt="Photo ${i + 1}" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover;" />
                <div style="position: absolute; bottom: 4px; right: 4px; background: rgba(0,0,0,0.7); color: white; padding: 4px 8px; border-radius: 4px; font-size: 12px; font-weight: 600;">
                  ${i + 1}
                </div>
                ${photoTimestamp ? `
                <div style="position: absolute; top: 4px; left: 4px; background: rgba(0,0,0,0.7); color: white; padding: 4px 8px; border-radius: 4px; font-size: 10px;">
                  ${photoTimestamp}
                </div>
                ` : ''}
              </div>
            `;
          }
          
          firstPageHTML += `
              </div>
            </div>
          `;
        }
        
        firstPageHTML += `</section>`;
        roomPages.push(firstPageHTML);
        
        // Additional photo pages if needed
        for (let p = 1; p < photoPages; p++) {
          const startIdx = p * PHOTOS_PER_PAGE;
          const endIdx = Math.min(startIdx + PHOTOS_PER_PAGE, photos.length);
          const pagePhotos = photos.slice(startIdx, endIdx);
          
          let photoPageHTML = `
            <section class="pdf-page" id="room-${index}-photos-${p}">
              ${this.generateHeaderSection(propertyDetails, currentPage++, totalPages, logoDataUrl, companyName, companyWebsite, companyPhone)}
              <div class="room-header" style="background: var(--primary-500) !important;">${room.name || 'Unnamed Room'} - Photos (continued)</div>
              <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin-top: 20px;">
          `;
          
          for (let i = startIdx; i < endIdx; i++) {
            const photo = pagePhotos[i - startIdx];
            const imageUrl = await this.getPhotoUrl(photo);
            const photoTimestamp = photo.timestamp ? new Date(photo.timestamp).toLocaleString('en-GB', { 
              day: '2-digit', 
              month: '2-digit', 
              year: 'numeric', 
              hour: '2-digit', 
              minute: '2-digit' 
            }) : '';
            photoPageHTML += `
              <div style="position: relative; padding-top: 75%; background: #f3f4f6; border-radius: 8px; overflow: hidden;">
                <img src="${imageUrl}" alt="Photo ${i + 1}" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover;" />
                <div style="position: absolute; bottom: 4px; right: 4px; background: rgba(0,0,0,0.7); color: white; padding: 4px 8px; border-radius: 4px; font-size: 12px; font-weight: 600;">
                  ${i + 1}
                </div>
                ${photoTimestamp ? `
                <div style="position: absolute; top: 4px; left: 4px; background: rgba(0,0,0,0.7); color: white; padding: 4px 8px; border-radius: 4px; font-size: 10px;">
                  ${photoTimestamp}
                </div>
                ` : ''}
              </div>
            `;
          }
          
          photoPageHTML += `
              </div>
            </section>
          `;
          roomPages.push(photoPageHTML);
        }
        
        return roomPages.join('\n');
      }
      
      // Check-In/Check-Out: Full inventory items
      if (!room.items || room.items.length === 0) {
        return `
          <section class="pdf-page" id="room-${index}">
            ${this.generateHeaderSection(propertyDetails, currentPage++, totalPages, logoDataUrl, companyName, companyWebsite, companyPhone)}
            <div class="room-header" style="background: var(--primary-500) !important;">${room.name || 'Unnamed Room'}</div>
            <p class="text-center mt-4">No inventory items recorded for this room.</p>
          </section>
        `;
      }

      // Split items into pages if more than ITEMS_PER_PAGE
      const itemPages: InventoryItem[][] = [];
      for (let i = 0; i < room.items.length; i += ITEMS_PER_PAGE) {
        itemPages.push(room.items.slice(i, i + ITEMS_PER_PAGE));
      }

      // Generate a page for each chunk of items
      const pageHTMLs = itemPages.map((pageItems, pageIndex) => {
        const tableRows = pageItems.map((item: InventoryItem) => `
          <tr>
            <td class="category-cell">${item.category || 'N/A'}</td>
            <td class="quantity-cell">${item.quantity?.toString() || '1'}</td>
            <td class="condition-cell">${item.condition || 'N/A'}</td>
            <td class="notes-cell">${item.notes || ''}</td>
          </tr>
        `).join('');

        const roomTitle = itemPages.length > 1 
          ? `${room.name || 'Unnamed Room'} (${pageIndex + 1} of ${itemPages.length})`
          : room.name || 'Unnamed Room';

        // First page uses 'room-${index}' to match TOC links, subsequent pages add '-page-${pageIndex}'
        const pageId = pageIndex === 0 ? `room-${index}` : `room-${index}-page-${pageIndex}`;

        return `
          <section class="pdf-page" id="${pageId}">
            ${this.generateHeaderSection(propertyDetails, currentPage++, totalPages, logoDataUrl, companyName, companyWebsite, companyPhone)}
            <div class="room-header" style="background: var(--primary-500) !important;">${roomTitle}</div>
            <div class="inventory-table-container">
              <table class="modern-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Qty</th>
                    <th>Condition</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  ${tableRows}
                </tbody>
              </table>
            </div>
          </section>
        `;
      });
      
      return pageHTMLs.join('');
    });
    
    const allRoomHTMLs = await Promise.all(roomHTMLPromises);
    return allRoomHTMLs.join('');
  }

  /**
   * Generate photos section with headers on each page
   */
  private static async generatePhotosSection(
    rooms: Room[], 
    propertyDetails: PropertyInventoryData['propertyDetails'], 
    startPage: number, 
    totalPages: number, 
    logoDataUrl?: string,
    companyName?: string,
    companyWebsite?: string,
    companyPhone?: string
  ): Promise<string> {
    const roomsWithPhotos = rooms.filter(room => 
      room.items?.some((item: InventoryItem) => 
        item.photos?.length > 0 && item.photos.some((p: RoomPhoto) => 
          p && !p.uploading && (p.url || p.localPath)
        )
      )
    );

    if (roomsWithPhotos.length === 0) {
      return '';
    }

    let photosHTML = '';
    let currentPage = startPage;

    for (const room of roomsWithPhotos) {
      const itemsWithPhotos = room.items.filter((item: InventoryItem) => 
        item?.photos?.length > 0 && item.photos.some((p: RoomPhoto) => 
          p && !p.uploading && (p.url || p.localPath)
        )
      );

      // Group items by category
      const itemsByCategory = itemsWithPhotos.reduce((acc: Record<string, InventoryItem[]>, item: InventoryItem) => {
        const category = item.category || 'Uncategorized';
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(item);
        return acc;
      }, {} as Record<string, InventoryItem[]>);

      for (const [category, categoryItems] of Object.entries(itemsByCategory) as [string, InventoryItem[]][]) {
        const photosSectionId = `photos-${room.name?.toLowerCase().replace(/\s+/g, '-') || 'room'}`;
        
        // Collect all photos from all items in this category
        const allPhotos: Array<{ photo: RoomPhoto, itemIndex: number, photoIndex: number }> = [];
        categoryItems.forEach((item: InventoryItem, itemIdx) => {
          const photos = item.photos?.filter((p: RoomPhoto) => 
            p && !p.uploading && (p.url || p.localPath)
          ) || [];
          photos.forEach((photo, photoIdx) => {
            allPhotos.push({ photo, itemIndex: itemIdx, photoIndex: photoIdx });
          });
        });

        if (allPhotos.length > 0) {
          // Process photos in batches of 4 (2x2 grid) - MAX 4 PHOTOS PER PAGE
          // Each batch gets its own <section class="pdf-page"> for proper pagination
          for (let i = 0; i < allPhotos.length; i += 4) {
            const photoBatch = allPhotos.slice(i, i + 4);
            const isFirstBatch = (i === 0);
            
            // Create a new page section for each batch of 4 photos
            photosHTML += `
              <section class="pdf-page" id="${isFirstBatch ? photosSectionId : photosSectionId + '-' + Math.floor(i / 4)}">
                ${this.generateHeaderSection(propertyDetails, currentPage++, totalPages, logoDataUrl, companyName, companyWebsite, companyPhone)}
                <div class="section-header">Property Photos</div>
                <div class="subsection-header">
                  <div class="subsection-title">${room.name || 'Unnamed Room'} - ${category}</div>
                  <div class="subsection-details">
                    ${categoryItems[0]?.condition ? `<div class="subsection-condition">Condition: ${categoryItems[0].condition}</div>` : ''}
                    ${categoryItems[0]?.notes ? `<div class="subsection-notes">Notes: ${categoryItems[0].notes}</div>` : ''}
                  </div>
                </div>
                <div class="photo-grid">
            `;
            
            for (const [index, photoData] of photoBatch.entries()) {
              const photoNumber = i + index + 1;
              const imageUrl = await this.getPhotoUrl(photoData.photo);
              
              photosHTML += `
                <div class="photo-container">
                  <div class="photo-wrapper">
                    <img src="${imageUrl}" alt="Photo ${photoNumber}" class="photo-image" />
                    <div class="photo-number">${photoNumber}</div>
                  </div>
                </div>
              `;
            }
            
            // Fill empty slots if batch has less than 4 photos (maintains 2x2 grid layout)
            for (let j = photoBatch.length; j < 4; j++) {
              photosHTML += '<div class="photo-empty-slot"></div>';
            }
            
            photosHTML += `
                </div>
              </section>
            `;
          }
        }
      }
    }

    return photosHTML;
  }

  /**
   * Generate table of contents items
   */
  private static generateContentsItems(
    data: PropertyInventoryData,
    totalPages: number,
    tocPageCount?: number
  ): Array<{title: string, page: number, id?: string}> {
    const { rooms, glossaryTerms, certificates, keys, meterReadings } = data;
    
    // If we don't know TOC page count yet, calculate it (for pre-calculation phase)
    if (tocPageCount === undefined) {
      // Temporarily calculate with page 3 start
      const tempItems: Array<{title: string, page: number, id?: string}> = [];
      let pageNum = 3;
      
      if (glossaryTerms) tempItems.push({ title: '', page: pageNum++, id: '' });
      if (certificates || keys || meterReadings) tempItems.push({ title: '', page: pageNum++, id: '' });
      rooms.forEach(() => tempItems.push({ title: '', page: pageNum++, id: '' }));
      
      const roomsWithPhotos = rooms.filter(room => 
        room.items?.some((item: any) => 
          item.photos?.length > 0 && item.photos.some((p: any) => 
            p && !p.uploading && (p.url || p.localPath)
          )
        )
      );
      roomsWithPhotos.forEach(() => tempItems.push({ title: '', page: pageNum++, id: '' }));
      tempItems.push({ title: '', page: pageNum++, id: '' }); // Signatures
      
      tocPageCount = Math.ceil(tempItems.length / 13);
    }
    
    // Start after Property Details (page 1) and TOC pages
    let pageNum = 1 + tocPageCount + 1; // 1 (property) + tocPages + 1 (start of content)
    const contentsItems: Array<{title: string, page: number, id?: string}> = [];
    
    // Add Glossary if exists
    if (glossaryTerms) {
      contentsItems.push({ title: 'Glossary of Terms', page: pageNum++, id: `page-${pageNum-1}` });
    }
    
    // Add Certificates/Keys/Meters if exists
    if (certificates || keys || meterReadings) {
      contentsItems.push({ title: 'Certificates, Keys & Meter Readings', page: pageNum++, id: `page-${pageNum-1}` });
    }
    
    // Add room inventories - ACCOUNT FOR MULTI-PAGE INVENTORIES
    rooms.forEach((room, index) => {
      const roomName = room.name || `Room ${index + 1}`;
      
      // Add TOC entry for this room
      contentsItems.push({ title: `${roomName} Inventory`, page: pageNum, id: `room-${index}` });
      
      // Calculate how many pages this room's inventory needs
      // Each page can hold up to 15 items (ITEMS_PER_INVENTORY_PAGE from generateRoomInventoriesSection)
      const itemCount = room.items?.length || 0;
      const ITEMS_PER_INVENTORY_PAGE = 15;
      const inventoryPagesForRoom = itemCount === 0 ? 1 : Math.ceil(itemCount / ITEMS_PER_INVENTORY_PAGE);
      
      // Advance page counter by actual number of pages this room needs
      pageNum += inventoryPagesForRoom;
    });
    
    // Add photo pages - MUST match generatePhotosSection logic exactly
    // Photos are grouped by CATEGORY, and each category is paginated separately
    // SKIP for mid-term reports (photos are inline with rooms)
    const isMidTerm = data.propertyDetails.reportType === 'midTerm';
    const roomsWithPhotos = rooms.filter(room => 
      room.items?.some((item: any) => 
        item.photos?.length > 0 && item.photos.some((p: any) => 
          p && !p.uploading && (p.url || p.localPath)
        )
      )
    );
    
    if (roomsWithPhotos.length > 0 && !isMidTerm) {
      roomsWithPhotos.forEach(room => {
        const roomName = room.name || 'Room';
        
        // Filter items with photos
        const itemsWithPhotos = room.items?.filter((item: any) => 
          item?.photos?.length > 0 && item.photos.some((p: any) => 
            p && !p.uploading && (p.url || p.localPath)
          )
        ) || [];
        
        // Group items by category (MUST match generatePhotosSection)
        const itemsByCategory = itemsWithPhotos.reduce((acc: Record<string, any[]>, item: any) => {
          const category = item.category || 'Uncategorized';
          if (!acc[category]) {
            acc[category] = [];
          }
          acc[category].push(item);
          return acc;
        }, {} as Record<string, any[]>);
        
        // Add TOC entry pointing to first photo page
        contentsItems.push({ 
          title: `${roomName} Photos`, 
          page: pageNum, 
          id: `photos-${room.name?.toLowerCase().replace(/\s+/g, '-') || 'room'}` 
        });
        
        console.log(`  ${roomName} Photos starts at page ${pageNum}`);
        
        // Calculate pages needed PER CATEGORY (each category paginated separately!)
        let totalPhotoPagesForRoom = 0;
        Object.entries(itemsByCategory).forEach(([category, categoryItems]: [string, any[]]) => {
          // Count photos in this category
          const categoryPhotoCount = categoryItems.reduce((count: number, item: any) => 
            count + (item.photos?.filter((p: any) => p && !p.uploading && (p.url || p.localPath))?.length || 0), 0
          );
          
          // Each category gets ceil(photos/4) pages
          const pagesForCategory = Math.ceil(categoryPhotoCount / 4);
          totalPhotoPagesForRoom += pagesForCategory;
        });
        
        // Advance page counter by actual number of pages this room's photos need
        pageNum += totalPhotoPagesForRoom;
      });
    }
    
    // Add signatures page - it comes after all photos
    contentsItems.push({ title: 'Signatures', page: pageNum, id: `signatures` });
    
    return contentsItems;
  }

  /**
   * Generate table of contents section with pagination (legacy - for single page TOC)
   */
  private static generateContentsSection(
    data: PropertyInventoryData,
    totalPages: number,
    tocPageCount: number
  ): string {
    const contentsItems = this.generateContentsItems(data, totalPages, tocPageCount);
    
    // Maximum items per page (considering reduced margins and spacing)
    // With 6mm margins, limit to 13 items for non-stretched appearance
    const ITEMS_PER_PAGE = 13;
    
    // Split items into pages if needed
    const pages: Array<Array<{title: string, page: number, id?: string}>> = [];
    for (let i = 0; i < contentsItems.length; i += ITEMS_PER_PAGE) {
      pages.push(contentsItems.slice(i, i + ITEMS_PER_PAGE));
    }
    
    // Generate HTML for each page
    return pages.map((pageItems, pageIndex) => `
      <div class="section-header">Table of Contents${pages.length > 1 ? ` (${pageIndex + 1} of ${pages.length})` : ''}</div>
      
      <div class="contents-section">
        <div class="contents-list">
          ${pageItems.map(item => `
            <div class="contents-item">
              <a href="#${item.id || 'page-' + item.page}" class="contents-link">
                <span class="contents-title">${item.title}</span>
                <span class="contents-dots"></span>
                <span class="contents-page">${item.page}</span>
              </a>
            </div>
          `).join('')}
        </div>
      </div>
      ${pageIndex < pages.length - 1 ? '<div class="page-break"></div>' : ''}
    `).join('');
  }

  /**
   * Generate table of contents pages with headers
   */
  private static generateContentsPagesSection(
    data: PropertyInventoryData,
    propertyDetails: PropertyInventoryData['propertyDetails'],
    startPage: number,
    totalPages: number,
    logoDataUrl?: string,
    companyName?: string,
    companyWebsite?: string,
    companyPhone?: string
  ): string {
    // Calculate TOC page count
    const tempItems = this.generateContentsItems(data, 0);
    const tocPageCount = Math.ceil(tempItems.length / 13);
    
    const contentsItems = this.generateContentsItems(data, totalPages, tocPageCount);
    
    // Maximum items per page
    const ITEMS_PER_PAGE = 13;
    
    // Split items into pages if needed
    const pages: Array<Array<{title: string, page: number, id?: string}>> = [];
    for (let i = 0; i < contentsItems.length; i += ITEMS_PER_PAGE) {
      pages.push(contentsItems.slice(i, i + ITEMS_PER_PAGE));
    }
    
    // Generate HTML for each TOC page with headers
    return pages.map((pageItems, pageIndex) => `
      <section class="pdf-page" id="page-${startPage + pageIndex}">
        ${this.generateHeaderSection(propertyDetails, startPage + pageIndex, totalPages, logoDataUrl, companyName, companyWebsite, companyPhone)}
        <div class="section-header">Table of Contents${pages.length > 1 ? ` (${pageIndex + 1} of ${pages.length})` : ''}</div>
        
        <div class="contents-section">
          <div class="contents-list">
            ${pageItems.map(item => `
              <div class="contents-item">
                <a href="#${item.id || 'page-' + item.page}" class="contents-link">
                  <span class="contents-title">${item.title}</span>
                  <span class="contents-dots"></span>
                  <span class="contents-page">${item.page}</span>
                </a>
              </div>
            `).join('')}
          </div>
        </div>
      </section>
    `).join('\n');
  }

  /**
   * Get signature date - use provided date or current date if signature exists
   */
  private static getSignatureDate(providedDate?: string, signatureData?: string): string {
    if (providedDate) {
      return providedDate;
    }
    
    // If signature exists but no date provided, use current date
    if (signatureData) {
      return new Date().toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit', 
        year: 'numeric'
      });
    }
    
    // No signature, return blank line
    return '________________';
  }

  /**
   * Generate signatures section
   */
  private static generateSignaturesSection(
    signatures?: PropertyInventoryData['signatures'],
    colors?: ReturnType<typeof HTMLInventoryPDFService.generateColorPalette>,
    propertyDetails?: PropertyInventoryData['propertyDetails'],
    companyName?: string,
    companyWebsite?: string,
    companyPhone?: string,
    companyEmail?: string,
    companyAddress?: string
  ): string {
    // Default to teal if colors not provided
    const safeColors = colors || this.generateColorPalette('#009688');
    return `
      <div class="section-header">Signatures</div>
      
      <div class="signature-disclaimer">
        <div class="signature-disclaimer-text">
          <strong>Important Notice:</strong> By signing this document, you agree that all items in this inventory have been inspected and the condition recorded reflects the state at the time of check-in. Any alterations or omissions must be reported within 7 days of taking occupancy or this inventory will be accepted as an accurate record.
        </div>
      </div>
      
      <div class="signatures-section">
        <div class="signature-box">
          <div style="font-size: 16px; color: #1f2937; font-weight: 700; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.05em; padding: 8px 0; display: block; text-align: left; border-bottom: 2px solid ${safeColors[500]}; position: relative; background: transparent;">Assessor Signature</div>
          <div class="signature-image">
            ${signatures?.assessor ? `<img src="${signatures.assessor}" alt="Assessor Signature" style="max-width: 100%; max-height: 108px; object-fit: contain;" />` : ''}
          </div>
          <div class="signature-date">Date: ${this.getSignatureDate(signatures?.assessorDate, signatures?.assessor)}</div>
        </div>
        <div class="signature-box">
          <div style="font-size: 16px; color: #1f2937; font-weight: 700; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.05em; padding: 8px 0; display: block; text-align: left; border-bottom: 2px solid ${safeColors[500]}; position: relative; background: transparent;">Customer Signature</div>
          <div class="signature-image">
            ${signatures?.customer ? `<img src="${signatures.customer}" alt="Customer Signature" style="max-width: 100%; max-height: 108px; object-fit: contain;" />` : ''}
          </div>
          <div class="signature-date">Date: ${this.getSignatureDate(signatures?.customerDate, signatures?.customer)}</div>
        </div>
      </div>

      <!-- Disclaimer Section -->
      <div class="disclaimer-section">
        <div class="disclaimer-title">Important Disclaimer</div>
        <div class="disclaimer-text">
          This property inventory report is prepared as a record of the condition of the property and its contents at the time of inspection. 
          The report is based on visual inspection only and does not constitute a structural survey or warranty. All descriptions and assessments 
          are made to the best of our professional knowledge and belief. This document should be read in conjunction with the tenancy agreement 
          and any additional terms and conditions. The company accepts no liability for any loss, damage, or expense arising from reliance on 
          this report beyond the statutory obligations. All photographs and documentation remain the property of the issuer of the report.
        </div>
      </div>

      <!-- Contact Details Section - Only show if company details are provided (not standard plan) -->
      ${(companyName || companyWebsite || companyPhone || companyEmail || companyAddress) ? `
      <div class="contact-section">
        <div class="contact-title">Contact Information</div>
        <div class="contact-details">
          ${companyName ? `
          <div class="contact-item">
            <span class="contact-label">Company:</span>
            <span class="contact-value">${companyName}</span>
          </div>
          ` : ''}
          ${companyWebsite ? `
          <div class="contact-item">
            <span class="contact-label">Website:</span>
            <span class="contact-value">${companyWebsite}</span>
          </div>
          ` : ''}
          ${companyPhone ? `
          <div class="contact-item">
            <span class="contact-label">Phone:</span>
            <span class="contact-value">${companyPhone}</span>
          </div>
          ` : ''}
          ${companyEmail ? `
          <div class="contact-item">
            <span class="contact-label">Email:</span>
            <span class="contact-value">${companyEmail}</span>
          </div>
          ` : ''}
          ${companyAddress ? `
          <div class="contact-item">
            <span class="contact-label">Address:</span>
            <span class="contact-value">${companyAddress}</span>
          </div>
          ` : ''}
        </div>
      </div>
      ` : ''}
    `;
  }

  /**
   * Show export format selection modal
   */
  private static async showExportModal(): Promise<'pdf' | 'html' | 'both' | 'cancel'> {
    return new Promise<'pdf' | 'html' | 'both' | 'cancel'>((resolve) => {
        const backdrop = document.createElement('div');
        backdrop.className = 'fixed inset-0 bg-black bg-opacity-50 z-[10000] flex items-center justify-content-center';
        backdrop.style.animation = 'fadeIn 0.2s ease-out';
        
        const modal = document.createElement('div');
        modal.className = 'fixed bg-white rounded-2xl shadow-2xl z-[10001] max-w-2xl w-full mx-4';
        modal.style.cssText = `
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          animation: slideUp 0.3s ease-out;
        `;
        
        const style = document.createElement('style');
        style.textContent = `
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { transform: translate(-50%, -48%); opacity: 0; }
            to { transform: translate(-50%, -50%); opacity: 1; }
          }
        `;
        document.head.appendChild(style);
        
        modal.innerHTML = `
          <div class="p-8">
            <button id="close-modal" class="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer border-0">
              <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div class="flex flex-col items-center mb-6">
              <div class="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </div>
              <h2 class="text-2xl font-bold text-gray-900 mb-2">Export Property Inventory</h2>
              <p class="text-gray-600 text-sm">Choose your preferred format below</p>
            </div>
            <div class="space-y-3">
              <button id="export-pdf" class="w-full p-5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl transition-all duration-200 text-left border-0 cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-[1.02]">
                <div class="flex items-center gap-4">
                  <div class="text-3xl">📄</div>
                  <div class="flex-1">
                    <div class="font-semibold text-base">PDF Document</div>
                    <div class="text-xs text-blue-100 mt-1">Professional format • Print-ready • Official distribution</div>
                  </div>
                  <svg class="w-6 h-6 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
              
              <button id="export-html" class="w-full p-5 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl transition-all duration-200 text-left border-0 cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-[1.02]">
                <div class="flex items-center gap-4">
                  <div class="text-3xl">🌐</div>
                  <div class="flex-1">
                    <div class="flex items-center gap-2">
                      <span class="font-semibold text-base">Interactive HTML</span>
                      <span class="px-2 py-0.5 bg-white bg-opacity-25 rounded text-xs font-bold">RECOMMENDED</span>
                    </div>
                    <div class="text-xs text-green-100 mt-1">✨ Works offline • Clickable photos • Modern design</div>
                  </div>
                  <svg class="w-6 h-6 text-green-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
              
              <button id="export-both" class="w-full p-5 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-xl transition-all duration-200 text-left border-0 cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-[1.02]">
                <div class="flex items-center gap-4">
                  <div class="text-3xl">📦</div>
                  <div class="flex-1">
                    <div class="font-semibold text-base">Both Formats</div>
                    <div class="text-xs text-purple-100 mt-1">Get PDF + HTML for maximum flexibility</div>
                  </div>
                  <svg class="w-6 h-6 text-purple-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
            </div>
            
            <div class="mt-6 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border-l-4 border-blue-500">
              <div class="flex items-start gap-3">
                <div class="text-2xl">💡</div>
                <div class="flex-1">
                  <div class="text-sm font-semibold text-blue-900 mb-1">Pro Tip</div>
                  <div class="text-xs text-blue-800 leading-relaxed">HTML format includes all photos embedded - works 100% offline with no internet connection needed!</div>
                </div>
              </div>
            </div>
          </div>
        `;
        
        document.body.appendChild(backdrop);
        document.body.appendChild(modal);
        
        
        const pdfBtn = modal.querySelector('#export-pdf') as HTMLElement;
        const htmlBtn = modal.querySelector('#export-html') as HTMLElement;
        const bothBtn = modal.querySelector('#export-both') as HTMLElement;
        const closeBtn = modal.querySelector('#close-modal') as HTMLElement;
        
        const cleanup = () => {
          document.body.removeChild(backdrop);
          document.body.removeChild(modal);
          document.head.removeChild(style);
        };
        
        pdfBtn.onclick = () => { cleanup(); resolve('pdf'); };
        htmlBtn.onclick = () => { cleanup(); resolve('html'); };
        bothBtn.onclick = () => { cleanup(); resolve('both'); };
        closeBtn.onclick = () => { cleanup(); resolve('cancel'); }; // Cancel export
    });
  }

  /**
   * Show progress modal and return controls
   */
  private static showProgressModal() {
    const progressDiv = document.createElement('div');
    progressDiv.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      padding: 35px 45px;
      border-radius: 16px;
      box-shadow: 0 20px 60px rgba(0,0,0,0.4);
      z-index: 99999;
      min-width: 400px;
      text-align: center;
      color: white;
    `;
    
    const backdrop = document.createElement('div');
    backdrop.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.6);
      backdrop-filter: blur(4px);
      z-index: 10000;
      animation: fadeIn 0.2s ease-out;
    `;
    document.body.appendChild(backdrop);
    
    const isCancelledRef = { current: false };
    const startTime = Date.now();
    const modalStartTime = Date.now(); // Track when modal opened for minimum display time
    let lastUpdateTime = startTime;
    let estimatedTotalTime = 0;
    let previousPercent = 0;
    
    progressDiv.innerHTML = `
      <div style="display: inline-block; width: 64px; height: 64px; background: rgba(255, 255, 255, 0.15); border-radius: 16px; display: flex; align-items: center; justify-content: center; margin-bottom: 24px; box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2); backdrop-filter: blur(10px); animation: pulse 2s ease-in-out infinite;">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
          <polyline points="10 9 9 9 8 9"></polyline>
        </svg>
      </div>
      <div style="font-size: 24px; font-weight: 700; color: white; margin-bottom: 10px; letter-spacing: -0.02em; text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);">
        Generating PDF Report
      </div>
      <div style="font-size: 15px; color: rgba(255, 255, 255, 0.9); margin-bottom: 28px; min-height: 22px; font-weight: 500;" id="progress-text">
        Preparing pages...
      </div>
      <div style="width: 100%; height: 14px; background: rgba(255, 255, 255, 0.2); border-radius: 10px; overflow: hidden; box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.15); backdrop-filter: blur(5px);">
        <div id="progress-bar" style="width: 0%; height: 100%; background: linear-gradient(90deg, #4ade80 0%, #22c55e 50%, #16a34a 100%); background-size: 200% 100%; transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1); animation: shimmer 2s linear infinite; border-radius: 10px; box-shadow: 0 0 10px rgba(34, 197, 94, 0.5);"></div>
      </div>
      <div style="margin-top: 20px; font-size: 14px; font-weight: 600; color: rgba(255, 255, 255, 0.95); display: flex; align-items: center; justify-content: space-between; gap: 12px;">
        <div style="display: flex; align-items: center; gap: 8px;">
          <div style="width: 8px; height: 8px; background: #4ade80; border-radius: 50%; animation: pulse 1.5s ease-in-out infinite; box-shadow: 0 0 8px rgba(74, 222, 128, 0.6);"></div>
          <span id="progress-percent">0%</span>
        </div>
        <span id="progress-timer" style="color: rgba(255, 255, 255, 0.8); font-size: 13px;">Estimating...</span>
      </div>
      <button id="cancel-export-btn" style="margin-top: 28px; padding: 12px 28px; background: rgba(239, 68, 68, 0.9); color: white; border: none; border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.2s; box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4); backdrop-filter: blur(10px);" onmouseover="this.style.background='rgba(220, 38, 38, 1)'; this.style.transform='translateY(-2px)'; this.style.boxShadow='0 6px 16px rgba(239, 68, 68, 0.5)'" onmouseout="this.style.background='rgba(239, 68, 68, 0.9)'; this.style.transform='translateY(0)'; this.style.boxShadow='0 4px 12px rgba(239, 68, 68, 0.4)'">
        Cancel Export
      </button>
    `;
    
    const progressStyle = document.createElement('style');
    progressStyle.textContent = `
      @keyframes pulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.8; transform: scale(0.98); }
      }
      @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes fadeOut {
        from { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        to { opacity: 0; transform: translate(-50%, -50%) scale(0.95); }
      }
    `;
    document.head.appendChild(progressStyle);
    
    document.body.appendChild(progressDiv);
    
    // Add cancel button listener
    const cancelBtn = document.getElementById('cancel-export-btn');
    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => {
        isCancelledRef.current = true;
        const textEl = document.getElementById('progress-text');
        if (textEl) textEl.textContent = 'Cancelling export...';
        if (cancelBtn) cancelBtn.textContent = 'Cancelling...';
      });
    }
    
    const updateProgress = (percent: number, text: string) => {
      const bar = document.getElementById('progress-bar');
      const textEl = document.getElementById('progress-text');
      const timerEl = document.getElementById('progress-timer');
      
      // Smooth progress bar animation
      if (bar) {
        // Cap at 100% for display
        const displayPercent = Math.min(Math.round(percent), 100);
        bar.style.width = `${displayPercent}%`;
      }
      
      if (textEl) textEl.textContent = text;
      
      // Calculate time estimate based on progress
      if (timerEl && percent > 0 && percent < 100) {
        const now = Date.now();
        const elapsed = now - startTime;
        
        // Only update estimate if progress has meaningfully changed
        if (percent > previousPercent + 1) {
          const percentDone = percent / 100;
          estimatedTotalTime = elapsed / percentDone;
          previousPercent = percent;
        }
        
        // Calculate remaining time
        const remaining = Math.max(0, estimatedTotalTime - elapsed);
        const remainingSeconds = Math.ceil(remaining / 1000);
        
        if (remainingSeconds > 60) {
          const mins = Math.floor(remainingSeconds / 60);
          const secs = remainingSeconds % 60;
          timerEl.textContent = `Est. ${mins}m ${secs}s remaining`;
        } else if (remainingSeconds > 0) {
          timerEl.textContent = `Est. ${remainingSeconds}s remaining`;
        } else if (percent > 5) {
          timerEl.textContent = 'Almost done...';
        } else {
          timerEl.textContent = 'Estimating time...';
        }
      } else if (timerEl && percent >= 100) {
        timerEl.textContent = 'Complete!';
      }
    };
    
    return { progressDiv, backdrop, updateProgress, isCancelledRef, modalStartTime };
  }

  /**
   * Optimized photo page rendering using jsPDF direct drawing (bypasses html2canvas)
   * This renders photo pages 10-20x faster than html2canvas approach
   */
  private static async renderPhotoPagesDirect(
    pdf: any,
    rooms: Room[],
    propertyDetails: PropertyInventoryData['propertyDetails'],
    startPage: number,
    totalPages: number,
    primaryColor: string = '#009688',
    logoDataUrl?: string,
    companyName?: string,
    companyWebsite?: string,
    companyPhone?: string,
    updateProgress?: (percent: number, text: string) => void
  ): Promise<number> {
    let currentPage = startPage;
    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 15;
    
    const roomsWithPhotos = rooms.filter(room => 
      room.items?.some((item: InventoryItem) => 
        item.photos?.length > 0 && item.photos.some((p: RoomPhoto) => 
          p && !p.uploading && (p.url || p.localPath)
        )
      )
    );

    if (roomsWithPhotos.length === 0) {
      return currentPage;
    }

    let totalPhotoPages = 0;
    roomsWithPhotos.forEach(room => {
      const itemsWithPhotos = room.items.filter((item: InventoryItem) => 
        item?.photos?.length > 0 && item.photos.some((p: RoomPhoto) => 
          p && !p.uploading && (p.url || p.localPath)
        )
      );
      const itemsByCategory = itemsWithPhotos.reduce((acc: Record<string, InventoryItem[]>, item: InventoryItem) => {
        const category = item.category || 'Uncategorized';
        if (!acc[category]) acc[category] = [];
        acc[category].push(item);
        return acc;
      }, {} as Record<string, InventoryItem[]>);
      
      Object.values(itemsByCategory).forEach((categoryItems: InventoryItem[]) => {
        let photoCount = 0;
        categoryItems.forEach((item: InventoryItem) => {
          photoCount += (item.photos?.filter((p: RoomPhoto) => 
            p && !p.uploading && (p.url || p.localPath)
          ) || []).length;
        });
        totalPhotoPages += Math.ceil(photoCount / 4);
      });
    });

    let processedPhotoPages = 0;

    for (const room of roomsWithPhotos) {
      const itemsWithPhotos = room.items.filter((item: InventoryItem) => 
        item?.photos?.length > 0 && item.photos.some((p: RoomPhoto) => 
          p && !p.uploading && (p.url || p.localPath)
        )
      );

      const itemsByCategory = itemsWithPhotos.reduce((acc: Record<string, InventoryItem[]>, item: InventoryItem) => {
        const category = item.category || 'Uncategorized';
        if (!acc[category]) acc[category] = [];
        acc[category].push(item);
        return acc;
      }, {} as Record<string, InventoryItem[]>);

      for (const [category, categoryItems] of Object.entries(itemsByCategory)) {
        const allPhotos: Array<{ photo: RoomPhoto, itemIndex: number, photoIndex: number }> = [];
        categoryItems.forEach((item: InventoryItem, itemIdx) => {
          const photos = item.photos?.filter((p: RoomPhoto) => 
            p && !p.uploading && (p.url || p.localPath)
          ) || [];
          photos.forEach((photo, photoIdx) => {
            allPhotos.push({ photo, itemIndex: itemIdx, photoIndex: photoIdx });
          });
        });

        if (allPhotos.length > 0) {
          for (let i = 0; i < allPhotos.length; i += 4) {
            const photoBatch = allPhotos.slice(i, i + 4);
            
            if (currentPage > startPage) {
              pdf.addPage();
            }

            // Update progress
            processedPhotoPages++;
            if (updateProgress && processedPhotoPages % 5 === 0) {
              const progress = 20 + Math.floor((processedPhotoPages / totalPhotoPages) * 70);
              updateProgress(progress, `Rendering photo page ${processedPhotoPages}/${totalPhotoPages}...`);
            }

            // Draw header with logo and page info
            // Convert hex to RGB for jsPDF
            const headerRgb = this.hexToRgb(primaryColor);
            pdf.setFillColor(headerRgb.r, headerRgb.g, headerRgb.b);
            pdf.roundedRect(margin, margin, pageWidth - margin * 2, 18, 2, 2, 'F');
            
            if (logoDataUrl) {
              try {
                pdf.addImage(logoDataUrl, 'PNG', margin + 3, margin + 3, 20, 12);
              } catch (e) {
                console.warn('Failed to add logo:', e);
              }
            }
            
            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.text(propertyDetails.address || 'Property Address', margin + 28, margin + 9);
            
            pdf.setFontSize(9);
            pdf.setFont('helvetica', 'normal');
            const companyInfo = `Property Inventory Report • ${companyName || 'Company'}`;
            pdf.text(companyInfo, margin + 28, margin + 13.5);
            
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.text(`Page ${currentPage}`, pageWidth - margin - 15, margin + 10, { align: 'right' });
            
            // Draw "Property Photos" section header
            let yPos = margin + 24;
            pdf.setFillColor(headerRgb.r, headerRgb.g, headerRgb.b);
            pdf.roundedRect(margin, yPos, pageWidth - margin * 2, 10, 2, 2, 'F');
            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Property Photos', margin + 3, yPos + 6.5);
            
            // Draw room/category subsection
            yPos += 14;
            pdf.setFillColor(248, 249, 250);
            pdf.setDrawColor(229, 231, 235);
            pdf.roundedRect(margin, yPos, pageWidth - margin * 2, 12, 2, 2, 'FD');
            pdf.setTextColor(31, 41, 55);
            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'bold');
            pdf.text(`${room.name || 'Unnamed Room'} - ${category}`, margin + 3, yPos + 8);
            
            // Draw 2x2 photo grid
            yPos += 18;
            const gridWidth = pageWidth - margin * 2;
            const gridHeight = pageHeight - yPos - margin;
            const photoWidth = (gridWidth - 10) / 2;
            const photoHeight = (gridHeight - 10) / 2;
            
            for (let j = 0; j < photoBatch.length && j < 4; j++) {
              const photoData = photoBatch[j];
              const col = j % 2;
              const row = Math.floor(j / 2);
              const x = margin + col * (photoWidth + 5);
              const y = yPos + row * (photoHeight + 5);
              
              // Draw photo container
              pdf.setDrawColor(229, 231, 235);
              pdf.setLineWidth(0.5);
              pdf.roundedRect(x, y, photoWidth, photoHeight, 2, 2, 'S');
              
              // Add photo image
              const imageUrl = photoData.photo.url || photoData.photo.localPath || '';
              if (imageUrl) {
                try {
                  pdf.addImage(imageUrl, 'JPEG', x + 2, y + 2, photoWidth - 4, photoHeight - 4, undefined, 'FAST');
                } catch (error) {
                  console.warn(`Failed to add photo ${i + j + 1}:`, error);
                  // Draw placeholder
                  pdf.setTextColor(156, 163, 175);
                  pdf.setFontSize(10);
                  pdf.text('Photo unavailable', x + photoWidth / 2, y + photoHeight / 2, { align: 'center' });
                }
              }
              
              // Add photo number badge
              const photoNumber = i + j + 1;
              pdf.setFillColor(headerRgb.r, headerRgb.g, headerRgb.b);
              pdf.circle(x + photoWidth - 8, y + 8, 6, 'F');
              pdf.setTextColor(255, 255, 255);
              pdf.setFontSize(9);
              pdf.setFont('helvetica', 'bold');
              pdf.text(photoNumber.toString(), x + photoWidth - 8, y + 9, { align: 'center' });
            }
            
            currentPage++;
          }
        }
      }
    }

    return currentPage;
  }

  /**
   * Generate PDF from HTML using jsPDF and html2canvas
   */
  private static async generatePDFFromHTML(
    htmlContent: string,
    filename: string,
    options: HTMLInventoryOptions,
    userChoice: 'pdf' | 'html' | 'both',
    progressModal?: { progressDiv: HTMLElement | null; updateProgress: (progress: number, message: string) => void; isCancelledRef: { current: boolean } },
    photoStats?: { totalPhotoCount: number; totalPhotoSize: number },
    data?: PropertyInventoryData
  ): Promise<void> {
    try {
      // User choice already made - no modal needed here
      
      // Import libraries AFTER user makes choice (faster modal display)
      let jsPDF: any = null;
      let html2canvas: any = null;
      
      if (userChoice === 'pdf' || userChoice === 'both') {
        // Only import if needed
        jsPDF = (await import('jspdf')).default;
        html2canvas = (await import('html2canvas')).default;
      }
      
      // Generate filenames
      const baseFilename = filename.replace(/\.pdf$/, '');
      const pdfFilename = `${baseFilename}.pdf`;
      const htmlFilename = `${baseFilename}.html`;
      
      let pdf: any = null;
      let exportStartTime = 0;
      
      // Use existing modal or create new one for PDF generation
      let progressDiv: HTMLElement | null;
      let updateProgress: (percent: number, text: string) => void;
      let isCancelledRef: { current: boolean };
      
      if (progressModal) {
        // Use the existing modal from exportInventoryToPDF
        progressDiv = progressModal.progressDiv;
        updateProgress = progressModal.updateProgress;
        isCancelledRef = progressModal.isCancelledRef;
      } else {
        // Fallback: create modal here (shouldn't happen with new flow)
        const modal = this.showProgressModal();
        progressDiv = modal.progressDiv;
        updateProgress = modal.updateProgress;
        isCancelledRef = modal.isCancelledRef;
      }
      
      // Only generate PDF if needed
      if (userChoice === 'pdf' || userChoice === 'both') {
        // Note: No photo limit - interactive TOC makes large PDFs navigable
        // Photos are not embedded as base64, so memory usage is reasonable
        exportStartTime = performance.now();
        console.log('📄 Generating PDF...');
        console.log('⏱️ Export started at:', new Date().toLocaleTimeString());
        
        // Create temporary container for HTML content
        const tempContainer = document.createElement('div');
        tempContainer.innerHTML = htmlContent;
        tempContainer.style.position = 'absolute';
        tempContainer.style.left = '-9999px';
        tempContainer.style.top = '-9999px';
        tempContainer.style.width = '210mm'; // A4 width
        tempContainer.style.backgroundColor = 'white';
        tempContainer.style.padding = '0';
        tempContainer.style.margin = '0';
        document.body.appendChild(tempContainer);

        // Simplify HTML structure for faster rendering
        updateProgress(10, 'Optimizing for export...');
        
        // Remove complex nested elements
        const allPages = tempContainer.querySelectorAll('.pdf-page');
        allPages.forEach(page => {
          // Flatten unnecessary wrapper divs
          const wrappers = page.querySelectorAll('div > div > div');
          wrappers.forEach(wrapper => {
            if (wrapper.children.length === 1 && wrapper.children[0].tagName === 'DIV') {
              const child = wrapper.children[0];
              wrapper.replaceWith(child);
            }
          });
        });
        
        const exportStyles = document.createElement('style');
        exportStyles.textContent = `
          .pdf-page * {
            box-shadow: none !important;
            text-shadow: none !important;
            transition: none !important;
            animation: none !important;
            filter: none !important;
            backdrop-filter: none !important;
            transform: none !important;
            outline: none !important;
            border-radius: 8px !important;
            font-feature-settings: normal !important;
            -webkit-font-smoothing: antialiased !important;
          }
          .pdf-page {
            contain: layout style paint !important;
          }
        `;
        tempContainer.appendChild(exportStyles);
        
        tempContainer.offsetHeight;
        updateProgress(20, 'Ready to render');

        // Check for cancellation before starting
        if (isCancelledRef.current) {
          console.log('🚫 Export cancelled by user');
          throw new Error('Export cancelled by user');
        }

        // Create PDF
        pdf = new jsPDF({
          orientation: options.orientation || 'portrait',
          unit: 'mm',
          format: 'a4',
          compress: true
        });

        // Get all pages in the HTML content
        const pages = tempContainer.querySelectorAll('.pdf-page');
        
        if (pages.length === 0) {
          // If no .pdf-page elements, treat entire content as one page
          console.log('⚡ Rendering single-page document...');
          
          const canvas = await html2canvas(tempContainer, {
            scale: 2.5, // High quality for premium output
            useCORS: true,
            allowTaint: false,
            backgroundColor: 'white',
            logging: false,
            imageTimeout: 0,
            removeContainer: true,
            foreignObjectRendering: false,
            ignoreElements: (element: Element) => element.classList.contains('no-export'),
            width: tempContainer.scrollWidth,
            height: tempContainer.scrollHeight
          });
          
          // Use JPEG with high quality for crisp output
          const imgData = canvas.toDataURL('image/jpeg', 0.88);
          const pdfWidth = 210; // A4 width in mm
          const pdfHeight = 297; // A4 height in mm
          const imgWidth = pdfWidth;
          const imgHeight = (canvas.height * pdfWidth) / canvas.width;
          
          pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, Math.min(imgHeight, pdfHeight), undefined, 'FAST');
          console.log('✅ Single page rendered');
        } else {
          // Process pages sequentially for maximum speed and memory efficiency
          const pageArray = Array.from(pages) as HTMLElement[];
          const totalPages = pageArray.length;
          
          console.log(`📄 Processing ${totalPages} pages sequentially...`);
          
          // Optimized html2canvas settings for high quality and speed
          const canvasOptions = {
            scale: 2.5, // High quality for premium output
            useCORS: true,
            allowTaint: false,
            backgroundColor: 'white',
            logging: false,
            imageTimeout: 0,
            removeContainer: true,
            foreignObjectRendering: false,
            ignoreElements: (element: Element) => element.classList.contains('no-export')
          };
          
          // 🚀 PHOTO GRID TEMPLATE CACHING SYSTEM
          // Cache multiple templates based on photo count (1-4 photos per page)
          const photoGridTemplateCache: Map<number, {
            canvas: HTMLCanvasElement | null;
            pageStructure: string;
            imagePositions: Array<{x: number, y: number, width: number, height: number, numberX: number, numberY: number}>;
            pageNumberPosition: {x: number, y: number, width: number, height: number} | null;
            subsectionPositions: {
              title: {x: number, y: number, width: number, height: number} | null;
              condition: {x: number, y: number, width: number, height: number} | null;
              notes: {x: number, y: number, width: number, height: number} | null;
            };
          }> = new Map();
          
          // Helper: Check if page is a photo grid page
          const isPhotoGridPage = (page: HTMLElement): boolean => {
            return page.querySelector('.photo-grid') !== null;
          };
          
          // Helper: Extract images from photo grid page
          const extractPhotoGridImages = (page: HTMLElement): Array<{url: string, number: string}> => {
            const images: Array<{url: string, number: string}> = [];
            const photoContainers = page.querySelectorAll('.photo-container');
            photoContainers.forEach(container => {
              const img = container.querySelector('img') as HTMLImageElement;
              const numberDiv = container.querySelector('.photo-number');
              if (img && numberDiv) {
                images.push({
                  url: img.getAttribute('src') || '',
                  number: numberDiv.textContent || ''
                });
              }
            });
            return images;
          };
          
          // Helper: Get page structure hash (header + layout, excluding images)
          const getPageStructureHash = (page: HTMLElement): string => {
            const clone = page.cloneNode(true) as HTMLElement;
            // Remove all images to compare just structure
            clone.querySelectorAll('img').forEach(img => img.remove());
            clone.querySelectorAll('.photo-number').forEach(num => num.textContent = 'X');
            return clone.innerHTML.substring(0, 500); // First 500 chars for comparison
          };
          
          console.log('📊 Analyzing pages for photo grid caching optimization...');
          const photoGridPages: number[] = [];
          const photoCountMap: Map<number, number[]> = new Map(); // Map photo count to page indices
          
          pageArray.forEach((page, idx) => {
            if (isPhotoGridPage(page)) {
              photoGridPages.push(idx);
              const photoCount = extractPhotoGridImages(page).length;
              if (!photoCountMap.has(photoCount)) {
                photoCountMap.set(photoCount, []);
              }
              photoCountMap.get(photoCount)!.push(idx);
            }
          });
          
          console.log(`✅ Found ${photoGridPages.length} photo grid pages out of ${totalPages} total pages`);
          console.log(`📸 Photo distribution: ${Array.from(photoCountMap.entries()).map(([count, pages]) => `${count} photos: ${pages.length} pages`).join(', ')}`);
          console.log(`⚡ Template caching will save ~${Math.round(photoGridPages.length * 0.7)}s (estimated)`);
          
          // Track render times for better progress estimation
          const renderTimes: number[] = [];
          let accumulatedRenderTime = 0;
          const batchStartTime = performance.now();
          
          // Process each page one at a time for memory efficiency
          for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
            // Check for cancellation
            if (isCancelledRef.current) {
              console.log('🚫 Export cancelled by user');
              throw new Error('Export cancelled by user');
            }
            
            const page = pageArray[pageIndex];
            
            // Calculate weighted progress (early pages contribute less to avoid slow start perception)
            // Use exponential weighting: later pages contribute more to smooth out the curve
            const weight = pageIndex < 10 ? 0.5 : 1.0; // First 10 pages weighted at 50%
            const totalWeight = (Math.min(10, totalPages) * 0.5) + (Math.max(0, totalPages - 10) * 1.0);
            const currentWeight = renderTimes.reduce((sum, _, idx) => 
              sum + (idx < 10 ? 0.5 : 1.0), 0) + weight;
            const renderProgress = 20 + Math.floor((currentWeight / totalWeight) * 70);
            
            // Estimate time remaining based on actual render speeds
            let timeEstimate = '';
            if (renderTimes.length >= 3) {
              const avgTimePerPage = accumulatedRenderTime / renderTimes.length;
              const remainingPages = totalPages - pageIndex;
              const estimatedSeconds = Math.ceil((avgTimePerPage * remainingPages) / 1000);
              
              if (estimatedSeconds > 60) {
                const mins = Math.floor(estimatedSeconds / 60);
                const secs = estimatedSeconds % 60;
                timeEstimate = ` (~${mins}m ${secs}s remaining)`;
              } else {
                timeEstimate = ` (~${estimatedSeconds}s remaining)`;
              }
            }
            
            // Show helpful message for first few pages
            const initialSetupMsg = pageIndex < 5 ? ' (initial setup - speeds up after caching)' : '';
            
            // Update progress with enhanced information
            updateProgress(
              renderProgress, 
              `Rendering page ${pageIndex + 1} of ${totalPages}${timeEstimate}${initialSetupMsg}`
            );
            console.log(`⚡ Page ${pageIndex + 1}/${totalPages} - Progress: ${renderProgress}%`);
            
            const pageStartTime = performance.now();
            const isPhotoPage = isPhotoGridPage(page);
            
            try {
              let canvas: HTMLCanvasElement | null = null;
              
              // 🚀 PHOTO GRID TEMPLATE OPTIMIZATION
              if (isPhotoPage) {
                const currentImages = extractPhotoGridImages(page);
                const photoCount = currentImages.length;
                const cachedTemplate = photoGridTemplateCache.get(photoCount);
                
                // First page with this photo count: render fully and cache as template
                if (!cachedTemplate) {
                  console.log(`📸 Rendering TEMPLATE for ${photoCount}-photo grid (page ${pageIndex + 1})...`);
                  
                  // Calculate exact positions BEFORE rendering
                  const photoContainersForCache = page.querySelectorAll('.photo-container');
                  const imagePositions: Array<{x: number, y: number, width: number, height: number, numberX: number, numberY: number}> = [];
                  
                  photoContainersForCache.forEach((container) => {
                    const img = container.querySelector('.photo-image') as HTMLElement;
                    const numberDiv = container.querySelector('.photo-number') as HTMLElement;
                    
                    if (img && numberDiv) {
                      const imgRect = img.getBoundingClientRect();
                      const pageRect = page.getBoundingClientRect();
                      const numberRect = numberDiv.getBoundingClientRect();
                      
                      imagePositions.push({
                        x: (imgRect.left - pageRect.left) * canvasOptions.scale,
                        y: (imgRect.top - pageRect.top) * canvasOptions.scale,
                        width: imgRect.width * canvasOptions.scale,
                        height: imgRect.height * canvasOptions.scale,
                        numberX: (numberRect.left - pageRect.left + numberRect.width / 2) * canvasOptions.scale,
                        numberY: (numberRect.top - pageRect.top + numberRect.height / 2) * canvasOptions.scale
                      });
                    }
                  });
                  
                  // Also capture page number position for dynamic updating
                  let pageNumberPosition: {x: number, y: number, width: number, height: number} | null = null;
                  const pageNumberElement = page.querySelector('.header-page') as HTMLElement;
                  if (pageNumberElement) {
                    const pageRect = page.getBoundingClientRect();
                    const headerRect = pageNumberElement.getBoundingClientRect();
                    pageNumberPosition = {
                      x: (headerRect.left - pageRect.left) * canvasOptions.scale,
                      y: (headerRect.top - pageRect.top) * canvasOptions.scale,
                      width: headerRect.width * canvasOptions.scale,
                      height: headerRect.height * canvasOptions.scale
                    };
                  }
                  
                  // Capture subsection positions (room/category title, condition, notes)
                  const subsectionPositions = {
                    title: null as {x: number, y: number, width: number, height: number} | null,
                    condition: null as {x: number, y: number, width: number, height: number} | null,
                    notes: null as {x: number, y: number, width: number, height: number} | null
                  };
                  
                  const pageRect = page.getBoundingClientRect();
                  
                  const titleElement = page.querySelector('.subsection-title') as HTMLElement;
                  if (titleElement) {
                    const rect = titleElement.getBoundingClientRect();
                    subsectionPositions.title = {
                      x: (rect.left - pageRect.left) * canvasOptions.scale,
                      y: (rect.top - pageRect.top) * canvasOptions.scale,
                      width: rect.width * canvasOptions.scale,
                      height: rect.height * canvasOptions.scale
                    };
                  }
                  
                  const conditionElement = page.querySelector('.subsection-condition') as HTMLElement;
                  if (conditionElement) {
                    const rect = conditionElement.getBoundingClientRect();
                    subsectionPositions.condition = {
                      x: (rect.left - pageRect.left) * canvasOptions.scale,
                      y: (rect.top - pageRect.top) * canvasOptions.scale,
                      width: rect.width * canvasOptions.scale,
                      height: rect.height * canvasOptions.scale
                    };
                  }
                  
                  const notesElement = page.querySelector('.subsection-notes') as HTMLElement;
                  if (notesElement) {
                    const rect = notesElement.getBoundingClientRect();
                    subsectionPositions.notes = {
                      x: (rect.left - pageRect.left) * canvasOptions.scale,
                      y: (rect.top - pageRect.top) * canvasOptions.scale,
                      width: rect.width * canvasOptions.scale,
                      height: rect.height * canvasOptions.scale
                    };
                  }
                  
                  console.log(`📐 Calculated ${imagePositions.length} photo positions + page number + subsection positions from DOM`);
                  
                  // Create a BLANK template by temporarily hiding dynamic content
                  const imagesToHide: HTMLElement[] = [];
                  const photoNumbersToHide: HTMLElement[] = [];
                  const subsectionDetailsToHide: HTMLElement[] = [];
                  
                  // Hide all photo images
                  page.querySelectorAll('.photo-image').forEach(img => {
                    const el = img as HTMLElement;
                    el.style.visibility = 'hidden';
                    imagesToHide.push(el);
                  });
                  
                  // Hide all photo numbers
                  page.querySelectorAll('.photo-number').forEach(num => {
                    const el = num as HTMLElement;
                    el.style.visibility = 'hidden';
                    photoNumbersToHide.push(el);
                  });
                  
                  // Hide subsection details (room/category title, condition, notes)
                  page.querySelectorAll('.subsection-title, .subsection-condition, .subsection-notes').forEach(el => {
                    const element = el as HTMLElement;
                    element.style.visibility = 'hidden';
                    subsectionDetailsToHide.push(element);
                  });
                  
                  // Hide page number (reuse the element we already queried)
                  if (pageNumberElement) {
                    pageNumberElement.style.visibility = 'hidden';
                  }
                  
                  // Render BLANK template (containers only, no content) for caching
                  const blankTemplateCanvas = await html2canvas(page, {
                    ...canvasOptions,
                    width: page.scrollWidth,
                    height: page.scrollHeight
                  });
                  
                  // Restore visibility
                  imagesToHide.forEach(el => el.style.visibility = 'visible');
                  photoNumbersToHide.forEach(el => el.style.visibility = 'visible');
                  subsectionDetailsToHide.forEach(el => el.style.visibility = 'visible');
                  if (pageNumberElement) pageNumberElement.style.visibility = 'visible';
                  
                  // Now render the ACTUAL page WITH images for the PDF
                  canvas = await html2canvas(page, {
                    ...canvasOptions,
                    width: page.scrollWidth,
                    height: page.scrollHeight
                  });
                  
                  // Cache the BLANK template (not the actual page)
                  const pageStructure = getPageStructureHash(page);
                  photoGridTemplateCache.set(photoCount, {
                    canvas: blankTemplateCanvas, // Cache the blank one
                    pageStructure: pageStructure,
                    imagePositions: imagePositions,
                    pageNumberPosition: pageNumberPosition,
                    subsectionPositions: subsectionPositions
                  });
                  
                  console.log(`✅ Blank template cached for ${photoCount}-photo layout! Subsequent ${photoCount}-photo pages will be 5-10x faster`);
                } 
                // Subsequent pages with this photo count: use cached template
                else {
                  console.log(`⚡ Using CACHED ${photoCount}-photo template (page ${pageIndex + 1}) - fast path`);
                  
                  // Clone the cached canvas
                  const templateCanvas = cachedTemplate.canvas;
                  if (templateCanvas) {
                    canvas = document.createElement('canvas');
                    canvas.width = templateCanvas.width;
                    canvas.height = templateCanvas.height;
                    const ctx = canvas.getContext('2d');
                    
                    if (ctx) {
                      // Copy BLANK template to new canvas
                      ctx.drawImage(templateCanvas, 0, 0);
                      
                      // Draw page number (fresh, not patched) - match HTML format exactly
                      if (cachedTemplate.pageNumberPosition) {
                        const pageNumPos = cachedTemplate.pageNumberPosition;
                        
                        // Get the actual page number from the current page DOM
                        const headerPageDiv = page.querySelector('.header-page') as HTMLElement;
                        if (headerPageDiv) {
                          const pageText = headerPageDiv.textContent || '';
                          
                          // Draw page number - matching CSS: font-size: var(--text-xs) ~12px, opacity: 0.8, line-height: 1.3
                          ctx.globalAlpha = 0.8; // Match opacity
                          ctx.fillStyle = 'white';
                          ctx.font = `${Math.round(12 * canvasOptions.scale)}px helvetica`;
                          ctx.textAlign = 'right';
                          ctx.textBaseline = 'alphabetic';
                          // Position at bottom-right, matching header-website line alignment
                          ctx.fillText(pageText, pageNumPos.x + pageNumPos.width, pageNumPos.y + pageNumPos.height);
                          ctx.globalAlpha = 1.0; // Reset opacity
                        }
                      }
                      
                      // Draw subsection details (room/category title, condition, notes)
                      const subsectionPos = cachedTemplate.subsectionPositions;
                      
                      // Draw room/category title
                      if (subsectionPos.title) {
                        const titleElement = page.querySelector('.subsection-title') as HTMLElement;
                        if (titleElement) {
                          const titleText = titleElement.textContent || '';
                          ctx.fillStyle = '#1f2937'; // Dark gray for title
                          ctx.font = `bold ${Math.round(16 * canvasOptions.scale)}px helvetica`;
                          ctx.textAlign = 'left';
                          ctx.textBaseline = 'top';
                          ctx.fillText(titleText, subsectionPos.title.x, subsectionPos.title.y);
                        }
                      }
                      
                      // Draw condition
                      if (subsectionPos.condition) {
                        const conditionElement = page.querySelector('.subsection-condition') as HTMLElement;
                        if (conditionElement) {
                          const conditionText = conditionElement.textContent || '';
                          ctx.fillStyle = '#4b5563'; // Medium gray for condition
                          ctx.font = `${Math.round(13 * canvasOptions.scale)}px helvetica`;
                          ctx.textAlign = 'left';
                          ctx.textBaseline = 'top';
                          ctx.fillText(conditionText, subsectionPos.condition.x, subsectionPos.condition.y);
                        }
                      }
                      
                      // Draw notes
                      if (subsectionPos.notes) {
                        const notesElement = page.querySelector('.subsection-notes') as HTMLElement;
                        if (notesElement) {
                          const notesText = notesElement.textContent || '';
                          ctx.fillStyle = '#6b7280'; // Lighter gray for notes (italic style)
                          ctx.font = `italic ${Math.round(12 * canvasOptions.scale)}px helvetica`;
                          ctx.textAlign = 'left';
                          ctx.textBaseline = 'top';
                          ctx.fillText(notesText, subsectionPos.notes.x, subsectionPos.notes.y);
                        }
                      }
                      
                      // Update only the image content and numbers using cached positions
                      // This is MUCH faster than re-rendering entire page
                      const cachedPositions = cachedTemplate.imagePositions;
                      
                      if (cachedPositions.length === 0) {
                        console.warn('⚠️ No cached positions available, skipping image updates');
                      }
                      
                      // Load and draw images with CORS (browser cache makes this fast)
                      const imageLoadPromises = [];
                      for (let i = 0; i < currentImages.length && i < Math.min(photoCount, cachedPositions.length); i++) {
                        const pos = cachedPositions[i];
                        const imageData = currentImages[i];
                        if (!pos) continue;
                        
                        // Create promise for each image load (from cache or network)
                        const imagePromise = (async () => {
                          try {
                            // Always create new Image with CORS to avoid tainted canvas
                            const img = new Image();
                            img.crossOrigin = 'anonymous';
                            
                            // Load image (will be instant from browser cache if already loaded)
                            const loaded = await Promise.race([
                              new Promise<boolean>((resolve) => {
                                img.onload = () => resolve(true);
                                img.onerror = () => resolve(false);
                                img.src = imageData.url;
                              }),
                              // Reduced timeout since browser cache makes this instant
                              new Promise<boolean>((resolve) => setTimeout(() => resolve(false), 10000))
                            ]);
                            
                            // Draw image if loaded successfully (onto blank template)
                            if (loaded && img && img.complete && img.naturalWidth > 0) {
                              // Draw image using cached position
                              ctx.drawImage(img, pos.x, pos.y, pos.width, pos.height);
                            } else {
                              // Draw placeholder for failed/unavailable image
                              ctx.fillStyle = '#f3f4f6';
                              ctx.fillRect(pos.x, pos.y, pos.width, pos.height);
                              ctx.strokeStyle = '#d1d5db';
                              ctx.lineWidth = 2;
                              ctx.strokeRect(pos.x, pos.y, pos.width, pos.height);
                              
                              // Add "Photo unavailable" text
                              ctx.fillStyle = '#9ca3af';
                              ctx.font = `${Math.round(14 * canvasOptions.scale)}px helvetica`;
                              ctx.textAlign = 'center';
                              ctx.textBaseline = 'middle';
                              ctx.fillText('Photo unavailable', pos.x + pos.width / 2, pos.y + pos.height / 2);
                            }
                            
                            // Always draw photo number badge (fresh onto blank template)
                            const badgeRadius = 19 * canvasOptions.scale;
                            ctx.fillStyle = '#009688'; // Primary color
                            ctx.beginPath();
                            ctx.arc(pos.numberX, pos.numberY, badgeRadius, 0, Math.PI * 2);
                            ctx.fill();
                            ctx.fillStyle = 'white';
                            ctx.font = `bold ${Math.round(12 * canvasOptions.scale)}px helvetica`;
                            ctx.textAlign = 'center';
                            ctx.textBaseline = 'middle';
                            ctx.fillText(imageData.number, pos.numberX, pos.numberY);
                          } catch (e) {
                            console.warn(`❌ Failed to update photo ${i + 1}:`, e);
                          }
                        })();
                        
                        imageLoadPromises.push(imagePromise);
                      }
                      
                      // Wait for all images to load/render in parallel
                      await Promise.all(imageLoadPromises);
                    }
                  }
                }
              } 
              // Non-photo pages: render normally
              else {
                canvas = await html2canvas(page, {
                  ...canvasOptions,
                  width: page.scrollWidth,
                  height: page.scrollHeight
                });
              }
              
              if (canvas) {
                // Convert to JPEG immediately
                const imgData = canvas.toDataURL('image/jpeg', 0.88);
                
                // Calculate dimensions
                const pdfWidth = 210;
                const pdfHeight = 297;
                const imgWidth = pdfWidth;
                const imgHeight = (canvas.height * pdfWidth) / canvas.width;
                
                // Add to PDF
                if (pageIndex > 0) {
                  pdf.addPage();
                }
                pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, Math.min(imgHeight, pdfHeight), undefined, 'FAST');
                
                // Track render time for progress estimation
                const pageRenderTime = performance.now() - pageStartTime;
                renderTimes.push(pageRenderTime);
                accumulatedRenderTime += pageRenderTime;
                
                // Free memory (but keep template cache)
                const isTemplateCanvas = isPhotoPage && Array.from(photoGridTemplateCache.values()).some(t => t.canvas === canvas);
                if (!isTemplateCanvas) {
                  const ctx = canvas.getContext('2d');
                  if (ctx) {
                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                  }
                  canvas.width = 1;
                  canvas.height = 1;
                }
                
                // Log timing every 10 pages
                if ((pageIndex + 1) % 10 === 0) {
                  let cacheStatus = '';
                  if (isPhotoPage) {
                    const photoCount = extractPhotoGridImages(page).length;
                    const isCached = photoGridTemplateCache.has(photoCount) && canvas !== photoGridTemplateCache.get(photoCount)?.canvas;
                    cacheStatus = isCached ? ` (cached ${photoCount}-photo)` : '';
                  }
                  const avgTime = accumulatedRenderTime / renderTimes.length;
                  console.log(`  ⚡ Page ${pageIndex + 1}: ${(pageRenderTime / 1000).toFixed(2)}s${cacheStatus} | Avg: ${(avgTime / 1000).toFixed(2)}s`);
                }
              } else {
                console.warn(`Failed to render page ${pageIndex + 1}`);
                if (pageIndex > 0) {
                  pdf.addPage();
                }
                pdf.setFontSize(12);
                pdf.text(`Page ${pageIndex + 1} - Rendering Error`, 20, 30);
                
                // Track minimal time for failed page
                const pageRenderTime = performance.now() - pageStartTime;
                renderTimes.push(pageRenderTime);
                accumulatedRenderTime += pageRenderTime;
              }
            } catch (error) {
              console.error(`Error rendering page ${pageIndex + 1}:`, error);
              if (pageIndex > 0) {
                pdf.addPage();
              }
              pdf.setFontSize(12);
              pdf.text(`Page ${pageIndex + 1} - Rendering Error`, 20, 30);
              
              // Track minimal time for error page
              const pageRenderTime = performance.now() - pageStartTime;
              renderTimes.push(pageRenderTime);
              accumulatedRenderTime += pageRenderTime;
            }
          }
          
          // Cleanup: Free all template cache memory
          let cleanedTemplates = 0;
          photoGridTemplateCache.forEach((template, photoCount) => {
            if (template.canvas) {
              const ctx = template.canvas.getContext('2d');
              if (ctx) {
                ctx.clearRect(0, 0, template.canvas.width, template.canvas.height);
              }
              template.canvas.width = 1;
              template.canvas.height = 1;
              cleanedTemplates++;
            }
          });
          photoGridTemplateCache.clear();
          console.log(`🧹 Cleaned up ${cleanedTemplates} cached template(s)`);
          
          const totalRenderTime = performance.now() - exportStartTime;
          console.log(`✅ All pages rendered in ${(totalRenderTime / 1000).toFixed(2)}s`);
          if (photoGridPages.length > 0) {
            const avgPhotoPageTime = totalRenderTime / photoGridPages.length / 1000;
            console.log(`📸 Photo pages: ${photoGridPages.length} (avg ${avgPhotoPageTime.toFixed(3)}s per page)`);
            console.log(`💾 Templates used: ${Array.from(photoCountMap.keys()).sort().join(', ')} photos per page`);
          }
        }
        
        updateProgress(90, 'Finalizing PDF...');
        
        // Clean up temporary container
        document.body.removeChild(tempContainer);
        
        // Add interactive features (clickable TOC and bookmarks)
        if (data && data.propertyDetails) {
          updateProgress(92, 'Adding interactive features...');
          this.addInteractivePDFFeatures(pdf, data, data.propertyDetails);
        }
      }
      
      // Save PDF if needed
      if (userChoice === 'pdf' || userChoice === 'both') {
        const saveStartTime = performance.now();
        updateProgress?.(95, 'Saving PDF...');
        
        if (Capacitor.isNativePlatform()) {
          await this.savePDFMobile(pdf, pdfFilename);
        } else {
          await this.savePDFDesktop(pdf, pdfFilename);
        }
        
        const saveTime = performance.now() - saveStartTime;
        console.log(`💾 PDF saved in ${(saveTime / 1000).toFixed(2)}s`);
        
        const totalExportTime = performance.now() - exportStartTime;
        const minutes = Math.floor(totalExportTime / 60000);
        const seconds = ((totalExportTime % 60000) / 1000).toFixed(1);
        
        console.log(`\n✅ ===== EXPORT COMPLETE =====`);
        console.log(`📊 Total export time: ${minutes}m ${seconds}s`);
        console.log(`📄 Pages exported: ${pdf.internal.pages.length - 1}`);
        console.log(`⚡ Average time per page: ${(totalExportTime / (pdf.internal.pages.length - 1) / 1000).toFixed(2)}s`);
        console.log(`==============================\n`);
        
        updateProgress?.(100, `✓ Complete! (${minutes}m ${seconds}s)`);
        
        // Show success state
        if (progressDiv) {
          const iconDiv = progressDiv.querySelector('div[style*="width: 56px"]') as HTMLElement;
          if (iconDiv) {
            iconDiv.style.background = 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
            iconDiv.innerHTML = `
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            `;
          }
        }
        
        // Remove progress indicator and backdrop after a short delay
        const modalStartTime = Date.now();
        setTimeout(() => {
          const elapsed = Date.now() - modalStartTime;
          const minDisplayTime = 1500; // 1.5 seconds minimum
          const remainingTime = Math.max(0, minDisplayTime - elapsed);
          
          setTimeout(() => {
            const backdrop = document.querySelector('div[style*="backdrop-filter: blur(4px)"]');
            if (progressDiv && progressDiv.parentNode) {
              progressDiv.style.animation = 'fadeOut 0.2s ease-out';
              setTimeout(() => {
                progressDiv.parentNode?.removeChild(progressDiv);
              }, 200);
            }
            if (backdrop && backdrop.parentNode) {
              backdrop.parentNode.removeChild(backdrop);
            }
          }, remainingTime + 500); // Add extra 500ms for user to see completion
        }, 0);
      }
      
      // Save HTML if needed
      if (userChoice === 'html' || userChoice === 'both') {
        const photoCount = photoStats?.totalPhotoCount || 0;
        const photoSize = photoStats?.totalPhotoSize || 0;
        
        // Always embed images for full offline functionality
        console.log(`📦 Embedding ${photoCount} photos (${(photoSize / 1024 / 1024).toFixed(2)} MB) for offline use...`);
        
        // Progress range depends on whether this is HTML-only or combined export
        const progressStart = userChoice === 'html' ? 30 : 95;
        const progressRange = userChoice === 'html' ? 65 : 3; // HTML gets 30-95, PDF gets 95-98
        
        updateProgress?.(progressStart, `Embedding ${photoCount} photos for offline use...`);
        
        const embeddedHTML = await this.embedImagesInHTML(htmlContent, (progress) => {
          // Show embedding progress with appropriate range
          const embedProgress = progressStart + (progress / 100 * progressRange);
          updateProgress?.(embedProgress, `Embedding photos: ${Math.round(progress)}%`);
        });
        
        updateProgress?.(98, 'Saving HTML file...');
        
        const htmlBlob = new Blob([embeddedHTML], { type: 'text/html' });
        
        if (Capacitor.isNativePlatform()) {
          // Mobile: Save HTML with embedded images
          const photoCount = photoStats?.totalPhotoCount || 0;
          const htmlSizeMB = (embeddedHTML.length / 1024 / 1024).toFixed(2);
          
          console.log(`📱 Saving HTML with ${photoCount} embedded photos (${htmlSizeMB} MB)...`);
          
          const result = await Filesystem.writeFile({
            path: htmlFilename,
            data: embeddedHTML,
            directory: Directory.Documents,
            encoding: Encoding.UTF8
          });
          
          console.log('📱 HTML saved to Documents:', result.uri);
          console.log(`✅ HTML with ${photoCount} embedded photos saved (${htmlSizeMB} MB)`);
          
          // Open iOS Share Sheet to let user choose where to save/share
          // Image compression keeps file size manageable (~32MB) so Share API works
          await Share.share({
            title: 'Property Inventory Report (Offline)',
            text: `Complete offline inventory with ${photoCount} photos - ${htmlSizeMB} MB`,
            files: [result.uri],
            dialogTitle: 'Save or Share Inventory Report'
          });
          
          console.log('📱 Share sheet presented to user');
        } else {
          // Desktop: Download HTML
          const url = URL.createObjectURL(htmlBlob);
          const link = document.createElement('a');
          link.href = url;
          link.download = htmlFilename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          console.log('💻 HTML downloaded:', htmlFilename);
        }
        
        // Show completion for HTML-only export
        if (userChoice === 'html') {
          updateProgress?.(100, '✓ HTML file ready!');
          
          // Show success state
          if (progressDiv) {
            const iconDiv = progressDiv.querySelector('div[style*="width: 56px"]') as HTMLElement;
            if (iconDiv) {
              iconDiv.style.background = 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
              iconDiv.innerHTML = `
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              `;
            }
          }
          
          // Remove progress indicator and backdrop after a short delay
          setTimeout(() => {
            const backdrop = document.querySelector('div[style*="backdrop-filter: blur(4px)"]');
            if (progressDiv && progressDiv.parentNode) {
              progressDiv.style.animation = 'fadeOut 0.2s ease-out';
              setTimeout(() => {
                progressDiv.parentNode?.removeChild(progressDiv);
              }, 200);
            }
            if (backdrop && backdrop.parentNode) {
              backdrop.parentNode.removeChild(backdrop);
            }
          }, 1500);
        }
      }
      
    } catch (error) {
      console.error('Error generating PDF from HTML:', error);
      
      // Clean up progress modal on error or cancellation
      const progressDiv = document.querySelector('div[style*="position: fixed"][style*="50%"]');
      const backdrop = document.querySelector('div[style*="backdrop-filter: blur(4px)"]');
      
      if (progressDiv && progressDiv.parentNode) {
        progressDiv.parentNode.removeChild(progressDiv);
      }
      if (backdrop && backdrop.parentNode) {
        backdrop.parentNode.removeChild(backdrop);
      }
      
      // Clean up temp elements
      const tempContainer = document.querySelector('div[style*="left: -9999px"]');
      if (tempContainer && tempContainer.parentNode) {
        tempContainer.parentNode.removeChild(tempContainer);
      }
      
      throw error;
    }
  }

  /**
   * Embed all images in HTML as base64 data URLs for portability
   */
  private static async embedImagesInHTML(html: string, onProgress?: (progress: number) => void): Promise<string> {
    try {
      console.log('📦 Converting images to embedded base64 data URLs...');
      
      // Extract all image URLs from the HTML
      const imgRegex = /<img[^>]+src="([^">]+)"/g;
      const uniqueUrls = new Set<string>();
      let match;
      
      // Collect unique URLs first
      // Skip Supabase storage URLs - they're already public and don't need embedding
      while ((match = imgRegex.exec(html)) !== null) {
        const url = match[1];
        if (!url.startsWith('data:') && !url.includes('supabase.co/storage')) {
          uniqueUrls.add(url);
        }
      }
      
      const totalImages = uniqueUrls.size;
      const images: { url: string; dataUrl: string }[] = [];
      let processedCount = 0;
      
      console.log(`📸 Processing ${totalImages} unique images...`);
      
      // Process images with progress tracking
      let failedCount = 0;
      let failedUrls: Array<{url: string, error: string}> = [];
      
      for (const url of uniqueUrls) {
        try {
          console.log(`🔄 [IMAGE EMBED] Fetching: ${url.substring(0, 100)}`);
          console.log(`🔄 [IMAGE EMBED] Full URL: ${url}`);
          
          // Fetch and convert image to data URL with timeout
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout
          
          console.log(`📡 [IMAGE EMBED] Initiating fetch with CORS mode...`);
          
          // Add credentials and CORS headers for Supabase
          const response = await fetch(url, { 
            signal: controller.signal,
            mode: 'cors',
            credentials: 'omit'
          });
          clearTimeout(timeoutId);
          
          console.log(`📡 [IMAGE EMBED] Fetch complete. Status: ${response.status}, Type: ${response.headers.get('content-type')}`);
          
          // Check if response is OK (200-299)
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          
          const blob = await response.blob();
          console.log(`📦 [IMAGE EMBED] Blob created. Size: ${blob.size} bytes, Type: ${blob.type}`);
          
          // Validate blob is an image
          if (!blob.type.startsWith('image/')) {
            throw new Error(`Invalid content type: ${blob.type}`);
          }
          
          // Compress image to reduce memory usage and prevent iOS WebView crash
          // This maintains visual quality while dramatically reducing file size
          console.log(`🖼️ [IMAGE EMBED] Starting image load and compression...`);
          
          const dataUrl = await new Promise<string>((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            
            img.onerror = (error) => {
              console.error(`❌ [IMAGE EMBED] Image load error:`, error);
              reject(new Error('Image load failed'));
            };
            
            img.onload = () => {
              try {
                console.log(`✅ [IMAGE EMBED] Image loaded. Dimensions: ${img.width}x${img.height}`);
                
                // Limit max dimension to 1600px to save memory
                const MAX_DIMENSION = 1600;
                let width = img.width;
                let height = img.height;
                
                if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
                  if (width > height) {
                    height = Math.round((height * MAX_DIMENSION) / width);
                    width = MAX_DIMENSION;
                  } else {
                    width = Math.round((width * MAX_DIMENSION) / height);
                    height = MAX_DIMENSION;
                  }
                }
                
                // Create canvas and compress
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                
                if (!ctx) {
                  reject(new Error('Failed to get canvas context'));
                  return;
                }
                
                ctx.drawImage(img, 0, 0, width, height);
                
                // Compress to JPEG at 0.6 quality (good balance of size vs quality)
                // This reduces memory by ~70% while maintaining visual quality
                const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.6);
                console.log(`✅ [IMAGE EMBED] Compressed to ${compressedDataUrl.length} chars (${(compressedDataUrl.length/1024).toFixed(2)} KB)`);
                resolve(compressedDataUrl);
              } catch (err) {
                reject(err);
              }
            };
            
            img.onerror = () => reject(new Error('Failed to load image for compression'));
            
            // Load the blob into the image
            const blobUrl = URL.createObjectURL(blob);
            img.src = blobUrl;
          });
          
          images.push({ url, dataUrl });
          processedCount++;
          
          console.log(`✅ [IMAGE EMBED] Successfully embedded ${processedCount}/${totalImages}`);
          
          if (onProgress) {
            onProgress(Math.round((processedCount / totalImages) * 100));
          }
        } catch (error) {
          failedCount++;
          const errorMsg = (error as Error).message || 'Unknown error';
          console.error(`❌ [IMAGE EMBED] FAILED ${processedCount + 1}/${totalImages}:`, errorMsg);
          console.error(`   Full URL: ${url}`);
          console.error(`   Error details:`, error);
          failedUrls.push({ url: url.substring(0, 100), error: errorMsg });
          
          if (onProgress) {
            processedCount++;
            onProgress(Math.round((processedCount / totalImages) * 100));
          }
        }
      }
      
      // Log summary of failed images for debugging
      if (failedCount > 0) {
        console.warn(`\n⚠️ IMAGE EMBEDDING SUMMARY:`);
        console.warn(`   Total images: ${totalImages}`);
        console.warn(`   Successfully embedded: ${totalImages - failedCount}`);
        console.warn(`   Failed: ${failedCount}`);
        console.warn(`\n   Failed URLs:`);
        failedUrls.forEach((item, i) => {
          console.warn(`   ${i + 1}. ${item.url}`);
          console.warn(`      Error: ${item.error}`);
        });
      }
      
      console.log(`📸 Successfully embedded ${images.length}/${totalImages} images as base64 data URLs`);
      if (failedCount > 0) {
        console.warn(`⚠️  ${failedCount} images failed to load (likely deleted or invalid URLs)`);
      }
      
      // Replace all image URLs with data URLs
      let embeddedHTML = html;
      images.forEach(({ url, dataUrl }) => {
        // Escape special regex characters in URL
        const escapedUrl = url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`src="${escapedUrl}"`, 'g');
        embeddedHTML = embeddedHTML.replace(regex, `src="${dataUrl}"`);
      });
      
      return embeddedHTML;
    } catch (error) {
      console.error('❌ Error embedding images in HTML:', error);
      // Return original HTML if embedding fails
      return html;
    }
  }

  /**
   * Convert HTML to MHTML (Web Archive) format with embedded images
   */
  private static async convertHTMLToMHTML(html: string): Promise<Blob> {
    try {
      console.log('📦 Converting HTML to MHTML format...');
      
      // Extract all image URLs from the HTML
      const imgRegex = /<img[^>]+src="([^">]+)"/g;
      const images: { url: string; base64: string; contentType: string }[] = [];
      let match;
      
      while ((match = imgRegex.exec(html)) !== null) {
        const url = match[1];
        
        // Skip if already processed or if it's already a data URL
        if (url.startsWith('data:') || images.some(img => img.url === url)) {
          continue;
        }
        
        try {
          // Fetch and convert image to base64
          const response = await fetch(url);
          const blob = await response.blob();
          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          
          images.push({
            url,
            base64,
            contentType: blob.type || 'image/webp'
          });
        } catch (error) {
          console.warn(`Failed to fetch image: ${url}`, error);
        }
      }
      
      console.log(`📸 Embedded ${images.length} images in MHTML`);
      
      // Generate unique boundary for MIME multipart
      const boundary = `----=_NextPart_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Build MHTML content
      let mhtmlContent = `MIME-Version: 1.0
Content-Type: multipart/related; boundary="${boundary}"

This is a multi-part message in MIME format.

--${boundary}
Content-Type: text/html; charset="utf-8"
Content-Transfer-Encoding: quoted-printable
Content-Location: index.html

${html}

`;
      
      // Add each image as a separate MIME part
      for (let i = 0; i < images.length; i++) {
        const img = images[i];
        mhtmlContent += `--${boundary}
Content-Type: ${img.contentType}
Content-Transfer-Encoding: base64
Content-Location: ${img.url}

${img.base64}

`;
      }
      
      // Close the MIME multipart
      mhtmlContent += `--${boundary}--
`;
      
      console.log('✅ MHTML conversion complete');
      
      return new Blob([mhtmlContent], { type: 'application/x-mimearchive' });
    } catch (error) {
      console.error('❌ Error converting HTML to MHTML:', error);
      throw error;
    }
  }

  /**
   * Add mobile-optimized clickable links to existing HTML TOC - KEEPS ORIGINAL STYLING
   */
  private static addInteractivePDFFeatures(
    pdf: any,
    data: PropertyInventoryData,
    propertyDetails: PropertyInventoryData['propertyDetails']
  ): void {
    try {
      console.log('📱 Adding mobile-optimized links to HTML TOC (original styling preserved)...');
      
      // Use the SAME generateContentsItems that was used to create the HTML
      // This ensures the page numbers match exactly
      const totalPages = pdf.internal.getNumberOfPages();
      const tempItems = this.generateContentsItems(data, 0);
      const tocPageCount = Math.ceil(tempItems.length / 13);
      
      // Generate the EXACT same TOC items that are displayed in the HTML
      const tocItems = this.generateContentsItems(data, totalPages, tocPageCount);
      
      // Add mobile-optimized clickable overlays - LARGE, RELIABLE TOUCH TARGETS
      // iOS requires minimum 44pt (15.5mm) touch targets for reliable tapping
      // We'll use generous overlapping areas to create continuous tap zone
      
      const tocStartPage = 2;
      
      // ACTUAL MEASURED VALUES from PDF (calibrated):
      // Measured from multiple PDFs, converted from points to mm
      const firstItemTop = 47.4; // mm from top of page to first TOC item
      const itemHeight = 17.35; // mm between each TOC item (measured average)
      const itemsPerPage = 13; // Max items that fit per page
      
      // iOS-optimized: Large touch targets (16mm) with 1mm overlap for continuous tap zone
      const touchTargetHeight = 16; // 16mm = ~45pt, meets iOS guidelines with margin
      
      console.log(`📱 Interactive TOC: ${touchTargetHeight}mm touch targets, ${tocItems.length} items across ${Math.ceil(tocItems.length / itemsPerPage)} page(s)`);
      
      let clickableCount = 0;
      
      tocItems.forEach((item, index) => {
        const pageIndex = Math.floor(index / itemsPerPage);
        const tocPage = tocStartPage + pageIndex;
        const itemOnPage = index % itemsPerPage;
        
        // Skip if TOC page doesn't exist
        if (tocPage > pdf.internal.getNumberOfPages()) {
          console.warn(`⚠️ Skipping item ${index} - TOC page ${tocPage} doesn't exist`);
          return;
        }
        
        // Center of this item
        const itemCenter = firstItemTop + (itemOnPage * itemHeight) + (itemHeight / 2);
        
        // Large touch target centered on item (extends above and below)
        let touchTop = itemCenter - (touchTargetHeight / 2);
        
        // Ensure touch zone stays within page bounds (6mm top padding, 282mm usable)
        const pageBottom = 285; // A4 height - margin
        const touchBottom = touchTop + touchTargetHeight;
        
        // Clamp to page bounds
        if (touchTop < 50) touchTop = 50; // Don't overlap header
        if (touchBottom > pageBottom) {
          touchTop = pageBottom - touchTargetHeight;
        }
        
        pdf.setPage(tocPage);
        
        // Large, forgiving clickable area for reliable mobile tapping
        pdf.link(
          10,  // x: start well before edge
          touchTop, // y: centered on item with generous coverage
          190, // width: full page width for easy tapping
          touchTargetHeight, // height: 16mm = reliable iOS tap target
          { pageNumber: item.page }
        );
        
        clickableCount++;
        
        // Log first 3 and last 3 items for verification
        if (index < 3 || index >= tocItems.length - 3) {
          console.log(`  [${index}] "${item.title}" @ pg ${tocPage} → target pg ${item.page}`);
        }
      });
      
      console.log(`✅ Added ${clickableCount} clickable links to TOC`);
      
      // Add PDF bookmarks for sidebar navigation
      const outlineItems = tocItems.map(item => ({
        title: item.title,
        pageNumber: item.page
      }));
      
      if (pdf.outline && typeof pdf.outline === 'function') {
        pdf.outline(outlineItems);
      }
      
      console.log(`✅ Added ${tocItems.length} mobile-optimized links to HTML TOC (original styling preserved)`);
      
    } catch (error) {
      console.error('⚠️ Failed to add mobile links:', error);
      // Don't throw - TOC still readable even without links
    }
  }

  /**
   * Save PDF for mobile platforms
   */
  private static async savePDFMobile(pdf: any, filename: string): Promise<void> {
    try {
      const base64Data = pdf.output('datauristring').split(',')[1];
      
      const result = await Filesystem.writeFile({
        path: filename,
        data: base64Data,
        directory: Directory.Cache,
        recursive: true
      });
      
      await Share.share({
        title: 'Property Inventory Report (HTML)',
        text: 'Modern HTML-based property inventory report',
        url: result.uri,
        dialogTitle: 'Share Property Inventory Report'
      });
      
    } catch (error) {
      console.error('❌ Mobile PDF save failed:', error);
      throw error;
    }
  }

  /**
   * Save PDF for desktop
   */
  private static async savePDFDesktop(pdf: any, filename: string): Promise<void> {
    try {
      pdf.save(filename);
    } catch (error) {
      console.error('❌ Desktop PDF save failed:', error);
      throw error;
    }
  }
}
